/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.tr
import theme.getIconCircleFill
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLTextAreaElement
import kotlinx.html.dom.create
import kotlinx.html.*
import kotlinx.html.js.*
import org.w3c.dom.HTMLElement
import kotlin.js.Date
import models.TransactionContext
import org.w3c.dom.HTMLInputElement
import repository.ContextApiModel
import ml.getCategoryHierarchy
import ml.getSelectableCategories
import ml.SelectableCategory

class TransactionDetailComponent(
    private val transaction: Transaction,
    private val onBackClick: () -> Unit = {},
    private val onCategoryChanged: ((String, Boolean, List<String>?) -> Unit)? = null,
    private val onSplitTransaction: ((List<SplitItemData>) -> Unit)? = null,
    private val onDistributeTransaction: ((String, String) -> Unit)? = null,
    private val onUnsplitTransaction: (() -> Unit)? = null,
    private val onUndistributeTransaction: (() -> Unit)? = null,
    private val onUnsplitGrandparent: (() -> Unit)? = null,
    private val onGoalAssigned: ((models.SavingsGoal) -> Unit)? = null,
    private val onGoalRemoved: (() -> Unit)? = null,
    private val goalsRepository: repository.SavingsGoalsRepository? = null,
    private val apiService: ApiService? = null,
    private val isReadOnly: Boolean = false,
    // Default to standard (not Pro). A forgotten wire then shows the upsell to a
    // Pro user (harmless) rather than handing Pro features to a standard user.
    private val isProTier: Boolean = false,
    private val isTrialing: Boolean = false,
    private val onSubscribeClick: (() -> Unit)? = null,
    private val onExcludeChanged: ((Boolean) -> Unit)? = null,  // (newExcludedState) -> Unit
    private val onNavigateToParent: (() -> Unit)? = null,
    private val onContextChanged: (() -> Unit)? = null,
    private val onCategoryColorChanged: (() -> Unit)? = null,
    private val parentDate: String? = null,
    private val parentAmount: Double? = null,
    private var userCategoryColors: Map<String, String> = emptyMap(),
    // Seeded by the host so the header icon/color can look up user-made categories
    // on first paint (before the modal's own async load completes).
    private val initialCustomCategories: List<CustomCategoryData> = emptyList(),
) {
    // Show the "Pro" pill: standard AND trialing (not permanent Pro). isProTier
    // (= pro||trialing) still governs whether the feature is locked, so a
    // trialing user gets the working action WITH the pill — a "you'll lose this"
    // nudge — while a standard user gets the locked upsell card instead.
    private val showProTag: Boolean get() = !isProTier || isTrialing

    fun render(): HTMLElement {
        val t = WebTheme.current
        // Inject responsive CSS for 3-button action row (desktop: row, mobile: column)
        if (document.getElementById("wiz3r-txn-styles") == null) {
            val style = document.createElement("style") as HTMLElement
            style.id = "wiz3r-txn-styles"
            style.textContent = ".txn-action-row { display: flex; flex-direction: row; gap: 12px; } .txn-action-row > * { flex: 1; } @media (max-width: 600px) { .txn-action-row { flex-direction: column; } }"
            document.head?.appendChild(style)
        }


        val element = document.create.div {
            style = """
                display: flex;
                flex-direction: column;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                background: transparent;
            """

            // Header with back button and title
            div {
                style = """
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 20px 24px;
                    background: ${t.card};
                    border-bottom: 1px solid ${t.border};
                    position: sticky;
                    top: 0;
                    z-index: 100;
                """

                button {
                    style = """
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        background: none;
                        border: none;
                        color: ${t.accent};
                        font-size: 16px;
                        cursor: pointer;
                        padding: 8px 12px;
                        border-radius: 6px;
                        transition: background-color 0.2s;
                    """
                    onMouseOver = "this.style.backgroundColor='${t.border}'"
                    onMouseOut = "this.style.backgroundColor='transparent'"
                    onClickFunction = {
                        onBackClick()
                    }

                    +"← ${i18n.StringKey.NAV_BACK.tr()}"
                }

                h1 {
                    style = """
                        font-size: 24px;
                        font-weight: 600;
                        color: ${t.textPrimary};
                        margin: 0;
                        flex: 1;
                        text-align: center;
                        margin-right: 80px;
                    """
                    +StringKey.TRANSACTION_DETAILS_TITLE.tr()
                }
            }
            
            // Main content container
            div {
                style = """
                    flex: 1;
                    padding: 20px;
                    max-width: 600px;
                    margin: 0 auto;
                    width: 100%;
                """
                
                // Professional Transaction Header Card
                div {
                    style = """
                        background: ${t.card};
                        border-radius: 16px;
                        padding: 20px;
                        margin-bottom: 16px;
                        border: 1px solid ${if (transaction.isDebit) "${t.expenseRed}40" else "${t.incomeGreen}40"};
                    """

                    // Line 1: Full merchant name (no truncation)
                    div {
                        style = """
                            font-size: 20px;
                            font-weight: bold;
                            color: ${t.textPrimary};
                            margin-bottom: 16px;
                        """
                        +transaction.merchant
                    }

                    // Line 2: Icon + Amount (left), Date + Time stacked (right)
                    div {
                        style = """
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: 16px;
                        """

                        // Left side: Icon + Amount
                        div {
                            style = """
                                display: flex;
                                align-items: center;
                                gap: 12px;
                            """

                            div {
                                id = "txn-detail-icon"
                                // Use the Plaid code for colour + icon lookup; the localized
                                // `category` string never matches the English keyword rules.
                                val catLookup = transaction.categoryLevel1.ifEmpty { transaction.category }
                                val catColor = getCategoryColor(catLookup)
                                style = """
                                    width: 44px;
                                    height: 44px;
                                    border-radius: 50%;
                                    background-color: ${getIconCircleFill(catColor, t.isDark)};
                                    ${if (t.isDark) "border: 2px solid $catColor;" else ""}
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    color: ${t.iconStroke};
                                """
                                // Prefer the user's chosen icon for user-made (primary, sub) pairs.
                                val userIconSvg = findCustomIconId(
                                    initialCustomCategories,
                                    transaction.categoryLevel1,
                                    transaction.categoryLevel2.ifEmpty { null },
                                )?.let { utils.CustomCategoryIcons.renderSvg(it, size = 22) }
                                if (userIconSvg != null) {
                                    unsafe { +userIconSvg }
                                } else {
                                    unsafe { +CategoryIconSvg.renderForCategory(catLookup, size = 22) }
                                }
                            }

                            div {
                                style = """
                                    font-size: 28px;
                                    font-weight: bold;
                                    color: ${if (transaction.isDebit) t.expenseRed else t.incomeGreen};
                                """
                                +formatCurrency(transaction.amount)
                            }

                            if (transaction.isExcluded) {
                                span {
                                    style = """
                                        font-size: 11px;
                                        color: #FFA500;
                                        background: rgba(255, 165, 0, 0.12);
                                        border: 1px solid rgba(255, 165, 0, 0.3);
                                        border-radius: 6px;
                                        padding: 2px 8px;
                                        font-weight: 500;
                                    """
                                    +StringKey.TRANSACTION_EXCLUDED_BADGE.tr()
                                }
                            }
                        }

                        // Right side: Date + Time stacked
                        div {
                            style = """
                                display: flex;
                                flex-direction: column;
                                align-items: flex-end;
                                gap: 2px;
                            """

                            div {
                                style = "font-size: 13px; color: ${t.textSecondary};"
                                +formatFullDate(transaction.date)
                            }
                            div {
                                style = "font-size: 13px; color: ${t.textSecondary};"
                                +formatTime(transaction.date)
                            }
                        }
                    }

                    // Divider
                    div {
                        style = "border-top: 1px solid ${t.border}; margin-bottom: 16px;"
                    }

                    // Line 4: Category label + full category
                    div {
                        style = """
                            display: flex;
                            align-items: flex-start;
                            margin-bottom: 8px;
                        """

                        div {
                            style = "font-size: 13px; color: ${t.textSecondary}; width: 70px; flex-shrink: 0;"
                            +StringKey.TRANSACTION_CATEGORY.tr()
                        }

                        div {
                            id = "txn-detail-category-display"
                            style = "font-size: 14px; font-weight: 500; color: ${t.textPrimary};"
                            val l1 = transaction.categoryLevel1
                            val l2 = transaction.categoryLevel2
                            val displayCat = if (l1.isNotEmpty() && l2.isNotEmpty()) {
                                utils.CategoryUtils.formatTwoLevelCategoryLocalized(l1, l2)
                            } else if (l1.isNotEmpty()) {
                                utils.CategoryUtils.formatCategoryNameLocalized(l1)
                            } else {
                                transaction.category
                            }
                            +displayCat
                        }
                    }

                    // Line 5: Account + Type
                    div {
                        style = """
                            display: flex;
                            align-items: center;
                            margin-bottom: 8px;
                        """

                        div {
                            style = "font-size: 13px; color: ${t.textSecondary}; width: 70px; flex-shrink: 0;"
                            +StringKey.TRANSACTION_ACCOUNT.tr()
                        }

                        div {
                            style = "font-size: 14px; color: ${t.textPrimary};"
                            +"${transaction.account} • ${if (transaction.isDebit) StringKey.TRANSACTION_DEBIT.tr() else StringKey.TRANSACTION_CREDIT.tr()}"
                        }
                    }

                    // Line 6: Goal assignment (if assigned) — container always present for dynamic updates
                    div {
                        id = "goal-assignment-display"
                        if (transaction.hasGoalAssignment) {
                            style = "display: flex; align-items: center;"
                            div {
                                style = "font-size: 13px; color: ${t.textSecondary}; width: 70px; flex-shrink: 0;"
                                +StringKey.TRANSACTION_GOAL.tr()
                            }
                            div {
                                style = "display: flex; align-items: center; gap: 4px;"
                                span {
                                    style = "display: inline-flex; align-items: center;"
                                    unsafe {
                                        +CategoryIconSvg.renderGoalIcon(
                                            iconValue = transaction.assignedGoalIcon ?: "🎯",
                                            size = 14,
                                            color = "#66A3FF"
                                        )
                                    }
                                }
                                span {
                                    style = "font-size: 14px; font-weight: 500; color: #66A3FF;"
                                    +(transaction.assignedGoalName ?: "Assigned Goal")
                                }
                            }
                        } else {
                            style = "display: none;"
                        }
                    }

                    // Line 7: Context chips (if any assigned) — placeholder for dynamic updates
                    div {
                        id = "context-display-row"
                        style = if (transaction.contextIds.isNotEmpty()) {
                            "display: flex; align-items: flex-start; margin-top: 8px;"
                        } else {
                            "display: none;"
                        }
                        div {
                            style = "font-size: 13px; color: ${t.textSecondary}; width: 70px; flex-shrink: 0; padding-top: 2px;"
                            +StringKey.CONTEXT_ACTION_TITLE.tr()
                        }
                        div {
                            id = "context-chips-container"
                            style = "display: flex; flex-wrap: wrap; gap: 6px;"
                            // Chips rendered dynamically after contexts are loaded
                        }
                    }
                }

                val isSplitChild = transaction.childType == "SPLIT"
                val isDistributedChild = transaction.childType == "DISTRIBUTED"

                // Split Child — "Split from" info card
                if (isSplitChild) {
                    div {
                        style = """
                            background: rgba(255, 152, 0, 0.08);
                            border: 1px solid rgba(255, 152, 0, 0.3);
                            border-radius: 16px;
                            padding: 12px 16px;
                            margin-bottom: 12px;
                        """
                        div {
                            style = "color: #FF9800; font-size: 11px; font-weight: 600; margin-bottom: 4px;"
                            +"SPLIT FROM"
                        }
                        div {
                            style = "color: ${t.textPrimary}; font-size: 14px;"
                            +transaction.merchant.replace(Regex("""\s*\(\d+/\d+\)$"""), "")
                        }
                        if (parentDate != null) {
                            div {
                                style = "color: ${t.textSecondary}; font-size: 12px; margin-top: 2px;"
                                +parentDate
                            }
                        }
                        if (parentAmount != null) {
                            div {
                                style = "color: ${t.textSecondary}; font-size: 12px; margin-top: 2px;"
                                +formatCurrency(parentAmount)
                            }
                        }
                    }
                }

                // Distributed Child — "Distributed from" info card
                if (isDistributedChild) {
                    div {
                        style = """
                            background: rgba(255, 152, 0, 0.08);
                            border: 1px solid rgba(255, 152, 0, 0.3);
                            border-radius: 16px;
                            padding: 12px 16px;
                            margin-bottom: 12px;
                        """
                        if (transaction.distributionStartMonth != null && transaction.distributionEndMonth != null) {
                            val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
                            val startParts = transaction.distributionStartMonth!!.split("-")
                            val endParts = transaction.distributionEndMonth!!.split("-")
                            val startLabel = "${monthNames[startParts[1].toInt() - 1]} ${startParts[0]}"
                            val endLabel = "${monthNames[endParts[1].toInt() - 1]} ${endParts[0]}"
                            div {
                                style = "color: ${t.textSecondary}; font-size: 12px; margin-bottom: 4px;"
                                +"$startLabel – $endLabel"
                            }
                        }
                        div {
                            style = "color: #FF9800; font-size: 11px; font-weight: 600; margin-bottom: 4px;"
                            +"DISTRIBUTED FROM"
                        }
                        div {
                            style = "color: ${t.textPrimary}; font-size: 14px;"
                            +transaction.merchant.replace(Regex("""\s*\(\d+/\d+\)$"""), "")
                        }
                        if (parentDate != null) {
                            div {
                                style = "color: ${t.textSecondary}; font-size: 12px; margin-top: 2px;"
                                +parentDate
                            }
                        }
                        if (parentAmount != null) {
                            div {
                                style = "color: ${t.textSecondary}; font-size: 12px; margin-top: 2px;"
                                +formatCurrency(parentAmount)
                            }
                        }
                    }
                }

                // Parent status cards removed — parents are hidden from the list

                // Action Cards — each action in its own slim card
                if (isReadOnly) {
                    div {
                        style = """
                            background: ${t.card};
                            border-radius: 16px;
                            padding: 16px 20px;
                            margin-bottom: 8px;
                        """
                        div {
                            style = "display: flex; align-items: center; gap: 12px; cursor: pointer;"
                            onClickFunction = { onSubscribeClick?.invoke() }
                            span { style = "color: ${t.textSecondary}; font-size: 14px; flex: 1;"; +StringKey.READ_ONLY_ACTIONS_LOCKED.tr() }
                            span { style = "color: ${t.accent}; font-size: 14px; font-weight: 600;"; +StringKey.PAYWALL_SUBSCRIBE.tr() }
                        }
                    }
                } else if (NetworkMonitor.isOffline) {
                    div {
                        style = """
                            background: ${t.card};
                            border-radius: 16px;
                            padding: 16px 20px;
                            margin-bottom: 8px;
                        """
                        div {
                            style = "display: flex; align-items: center; gap: 12px;"
                            span { style = "color: ${t.textSecondary}; font-size: 16px;"; +"📡" }
                            span { style = "color: ${t.textSecondary}; font-size: 14px; flex: 1;"; +StringKey.OFFLINE_EDIT_BLOCKED.tr() }
                        }
                    }
                } else {
                    val isExcluded = transaction.isExcluded
                    val isMobile = ResponsiveUtils.isMobile()

                    // Simplified action rules: parents are hidden, only children and normal shown
                    // Detect nested cases: split-of-distributed and distributed-of-split
                    val isSplitOfDistributed = isSplitChild && transaction.parentTransactionId?.contains("_dist_") == true
                    val isDistributedOfSplit = isDistributedChild && transaction.parentTransactionId?.contains("_split_") == true
                    val canChangeCategory = true
                    val canSplit = !isSplitChild && !isDistributedOfSplit  // not if already part of a split
                    val canDistribute = !isDistributedChild && !isSplitOfDistributed  // not if already part of a distribution
                    // Only offer the exclude/include toggle when it actually changes a
                    // total. For an internal transfer not feeding a goal (e.g. a
                    // bank-transfer-labeled card payment), is_excluded changes nothing —
                    // the transfer rule already keeps it out of cash-flow — so hiding it
                    // avoids an Include button that would do nothing.
                    val canExclude = !transaction.isTransfer || transaction.hasGoalAssignment
                    val canUnsplit = isSplitChild || isDistributedOfSplit  // split children + distributed children of split (cascades)
                    val canUndistribute = isDistributedChild || isSplitOfDistributed  // distributed children + split children of distributed (cascades)

                    // On desktop we lay the six action cards out on a 3×2 grid. Cards in the
                    // same row share a height via grid-template-rows + height:100% on each
                    // card. On mobile we fall back to a single-column flow.
                    val cardMargin = if (isMobile) "8px" else "0"
                    val fillHeight = !isMobile

                    val renderChangeCategory: DIV.() -> Unit = {
                        if (canChangeCategory) {
                            createSlimActionCard(
                                StringKey.TRANSACTION_CHANGE_CATEGORY.tr(),
                                StringKey.TRANSACTION_CHANGE_CATEGORY_DESC.tr(),
                                marginBottom = cardMargin,
                                fillHeight = fillHeight
                            ) { showCategorySelector() }
                        }
                    }

                    val renderAssignGoal: DIV.() -> Unit = {
                        if (canChangeCategory && transaction.isDebit) {
                            createSlimActionCard(
                                StringKey.TRANSACTION_ASSIGN_GOAL.tr(),
                                StringKey.TRANSACTION_ASSIGN_GOAL_DESC.tr(),
                                marginBottom = cardMargin,
                                fillHeight = fillHeight
                            ) { showGoalSelectorModal() }
                        }
                    }

                    val renderContext: DIV.() -> Unit = {
                        // Context Assignment (Pro feature) — rendered dynamically once
                        // contexts are loaded from /api/contexts
                        div { id = "context-action-placeholder" }
                    }

                    val renderExclude: DIV.() -> Unit = {
                        if (canExclude) {
                            createSlimActionCard(
                                if (isExcluded) StringKey.TRANSACTION_INCLUDE.tr() else StringKey.TRANSACTION_EXCLUDE.tr(),
                                if (isExcluded) StringKey.TRANSACTION_INCLUDE_DESC.tr() else StringKey.TRANSACTION_EXCLUDE_DESC.tr(),
                                marginBottom = cardMargin,
                                fillHeight = fillHeight
                            ) {
                                onExcludeChanged?.invoke(!isExcluded)
                            }
                        }
                    }

                    val renderSplit: DIV.() -> Unit = {
                        if (canSplit && !isExcluded) {
                            if (isProTier || isTrialing) {
                                // Pro/trialing can use Split. Trialing still sees the
                                // "Pro" pill (showProTag) as a lose-it-at-trial-end
                                // nudge; a permanent Pro sees none. Standard hits the
                                // locked "Upgrade to Pro" card below.
                                createSlimActionCard(
                                    StringKey.TRANSACTION_SPLIT.tr(),
                                    StringKey.TRANSACTION_SPLIT_DESC.tr(),
                                    proBadge = showProTag,
                                    marginBottom = cardMargin,
                                    fillHeight = fillHeight
                                ) { showSplitModal() }
                            } else {
                                createProLockedActionCard(
                                    StringKey.TRANSACTION_SPLIT.tr(),
                                    StringKey.TRANSACTION_SPLIT_DESC.tr(),
                                    marginBottom = cardMargin,
                                    fillHeight = fillHeight
                                ) { onSubscribeClick?.invoke() }
                            }
                        }
                    }

                    val renderDistribute: DIV.() -> Unit = {
                        if (canDistribute && !isExcluded) {
                            if (isProTier || isTrialing) {
                                // Same as Split: trialing keeps the pill, permanent
                                // Pro doesn't, standard gets the locked card.
                                createSlimActionCard(
                                    StringKey.TRANSACTION_DISTRIBUTE.tr(),
                                    StringKey.TRANSACTION_DISTRIBUTE_DESC.tr(),
                                    proBadge = showProTag,
                                    marginBottom = cardMargin,
                                    fillHeight = fillHeight
                                ) { showDistributeModal() }
                            } else {
                                createProLockedActionCard(
                                    StringKey.TRANSACTION_DISTRIBUTE.tr(),
                                    StringKey.TRANSACTION_DISTRIBUTE_DESC.tr(),
                                    marginBottom = cardMargin,
                                    fillHeight = fillHeight
                                ) { onSubscribeClick?.invoke() }
                            }
                        }
                    }

                    val renderDestructive: DIV.() -> Unit = {
                        if (canUnsplit && !isExcluded) {
                            createSlimActionCard(StringKey.TRANSACTION_UNSPLIT.tr()) {
                                if (isDistributedOfSplit) {
                                    (onUnsplitGrandparent ?: onUnsplitTransaction)?.invoke()
                                } else {
                                    onUnsplitTransaction?.invoke()
                                }
                            }
                        }
                        if (canUndistribute && !isExcluded) {
                            createSlimActionCard(StringKey.TRANSACTION_UNDISTRIBUTE.tr()) {
                                (onUndistributeTransaction ?: onUnsplitTransaction)?.invoke()
                            }
                        }
                    }

                    if (isMobile) {
                        // Linear order: Change Category, Assign Goal, Context, Exclude,
                        // Split, Distribute, (Unsplit/Undistribute), then Note (outside).
                        renderChangeCategory()
                        renderAssignGoal()
                        renderContext()
                        renderExclude()
                        renderSplit()
                        renderDistribute()
                        renderDestructive()
                    } else {
                        // Desktop: 3×2 grid — primary group on left column, power group on
                        // right. Rows share height because grid-auto-rows is 1fr and each
                        // card sets height:100%.
                        div {
                            style = """
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                                grid-auto-rows: 1fr;
                                gap: 8px;
                                margin-bottom: 8px;
                            """
                            renderChangeCategory()  // row 1, col 1
                            renderExclude()         // row 1, col 2
                            renderAssignGoal()      // row 2, col 1
                            renderSplit()           // row 2, col 2
                            renderContext()         // row 3, col 1
                            renderDistribute()      // row 3, col 2
                        }
                        renderDestructive()
                    }
                }

                // Note Card — collapsed, at bottom. Uses the same colour as the
                // modal panel background so it blends in as its own subtle zone.
                val noteBg = "color-mix(in srgb, ${t.background}, ${if (t.isDark) "black 20%" else "black 7%"})"
                div {
                    id = "note-card"
                    style = """
                        background: $noteBg;
                        border-radius: 16px;
                        padding: 14px 20px;
                        margin-bottom: 8px;
                    """
                    val hasNote = !transaction.userNotes.isNullOrBlank()
                    div {
                        style = "cursor: pointer;"
                        onClickFunction = {
                            val card = document.getElementById("note-card") as? HTMLElement
                            val noteContent = card?.querySelector(".note-expand") as? HTMLElement
                            val noteLabel = card?.querySelector(".note-label") as? HTMLElement
                            if (card != null && noteContent != null && noteLabel != null) {
                                if (noteContent.style.display == "none" || noteContent.style.display.isBlank()) {
                                    noteContent.style.display = "block"
                                    noteLabel.style.display = "none"
                                } else {
                                    noteContent.style.display = "none"
                                    // Update label to reflect current note
                                    val ta = card.querySelector("#note-inline-textarea") as? HTMLTextAreaElement
                                    val noteText = ta?.value?.trim() ?: ""
                                    noteLabel.textContent = if (noteText.isNotBlank()) noteText else StringKey.TRANSACTION_NOTE_TAKE.tr()
                                    noteLabel.style.display = "inline"
                                }
                            }
                        }
                        span(classes = "note-label") {
                            style = "color: ${if (hasNote) t.textPrimary else t.textSecondary}; font-size: 15px; font-weight: ${if (hasNote) "400" else "500"}; ${if (hasNote && t.isDark) "opacity: 0.7;" else ""}"
                            +(if (hasNote) transaction.userNotes!! else StringKey.TRANSACTION_NOTE_TAKE.tr())
                        }
                    }
                    div(classes = "note-expand") {
                        style = "display: none; margin-top: 10px;"
                        textArea {
                            id = "note-inline-textarea"
                            attributes["maxlength"] = "200"
                            if (isReadOnly) attributes["readonly"] = ""
                            style = """
                                width: 100%;
                                min-height: 52px;
                                max-height: 120px;
                                background: transparent;
                                border: none;
                                border-bottom: 1px solid ${t.border};
                                color: ${t.textPrimary}; opacity: ${if (isReadOnly) "0.45" else "0.85"};
                                font-size: 14px;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                                resize: none;
                                outline: none;
                                padding: 0;
                                box-sizing: border-box;
                                overflow-y: auto;
                                ${if (isReadOnly) "cursor: not-allowed;" else ""}
                            """
                            placeholder = if (isReadOnly) "" else "Tap to add a note..."
                            +(transaction.userNotes ?: "")
                        }
                        p {
                            id = "note-inline-counter"
                            style = "text-align: right; color: ${t.textSecondary}; font-size: 12px; margin: 4px 0 0 0;"
                            +"${(transaction.userNotes?.length ?: 0)}/200"
                        }
                    }
                }
            }
        }.also { root ->
            // Wire up note textarea: counter update + auto-resize + debounce auto-save (edit mode only)
            if (!isReadOnly) {
                val noteTA = root.querySelector("#note-inline-textarea") as? HTMLTextAreaElement
                val noteCounter = root.querySelector("#note-inline-counter") as? HTMLElement
                var noteDebounce = 0
                noteTA?.addEventListener("input", {
                    val ta = it.target as? HTMLTextAreaElement ?: return@addEventListener
                    noteCounter?.textContent = "${ta.value.length}/200"
                    noteCounter?.style?.color = if (ta.value.length >= 200) "#FF3B30" else t.textSecondary
                    ta.style.height = "auto"
                    ta.style.height = "${ta.scrollHeight}px"
                    window.clearTimeout(noteDebounce)
                    noteDebounce = window.setTimeout({
                        MainScope().launch {
                            try { apiService?.updateTransactionNotes(transaction.id, ta.value) }
                            catch (e: Exception) { console.error("Note save failed: ${e.message}") }
                        }
                    }, 1000)
                })
            }

            // Parent children loading removed — parents are hidden from list
        }

        // Launch context loading after element is created
        MainScope().launch { loadAndRenderContexts(element) }

        return element
    }

    private fun DIV.createSlimActionCard(
        title: String,
        subtitle: String = "",
        proBadge: Boolean = false,
        bgColor: String? = null,
        bgHoverColor: String? = null,
        marginBottom: String = "8px",
        fillHeight: Boolean = false,
        onClick: () -> Unit
    ) {
        val t = WebTheme.current
        val bg = bgColor ?: t.card
        val hoverBg = bgHoverColor ?: t.iconCircleDefault
        div {
            style = """
                background: $bg;
                border-radius: 16px;
                padding: 14px 20px;
                margin-bottom: $marginBottom;
                cursor: pointer;
                transition: background 0.15s ease;
                ${if (fillHeight) "height: 100%; box-sizing: border-box;" else ""}
            """
            onMouseOver = "this.style.backgroundColor='$hoverBg'"
            onMouseOut = "this.style.backgroundColor='$bg'"
            onClickFunction = { onClick() }
            div {
                style = "display: flex; align-items: center; gap: 8px;"
                span {
                    style = "color: ${t.textPrimary}; font-size: 15px; font-weight: 500;"
                    +title
                }
                if (proBadge) {
                    span {
                        style = """
                            font-size: 9px; font-weight: 700; color: ${t.proBadge}; background: rgba(245, 158, 11, 0.15);
                            padding: 2px 6px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.5px;
                        """
                        +"Pro"
                    }
                }
            }
            if (subtitle.isNotEmpty()) {
                div {
                    style = "color: ${t.textSecondary}; font-size: 13px; margin-top: 2px;"
                    +subtitle
                }
            }
        }
    }

    private fun DIV.createProLockedActionCard(
        title: String,
        subtitle: String = "",
        bgColor: String? = null,
        bgHoverColor: String? = null,
        marginBottom: String = "8px",
        fillHeight: Boolean = false,
        onClick: () -> Unit
    ) {
        val t = WebTheme.current
        val bg = bgColor ?: t.card
        val hoverBg = bgHoverColor ?: t.iconCircleDefault
        div {
            style = """
                background: $bg;
                border-radius: 16px;
                padding: 14px 20px;
                margin-bottom: $marginBottom;
                cursor: pointer;
                transition: background 0.15s ease;
                opacity: 0.7;
                ${if (fillHeight) "height: 100%; box-sizing: border-box;" else ""}
            """
            onMouseOver = "this.style.backgroundColor='$hoverBg'"
            onMouseOut = "this.style.backgroundColor='$bg'"
            onClickFunction = { onClick() }
            div {
                style = "display: flex; align-items: center; justify-content: space-between;"
                span {
                    style = "color: ${t.textSecondary}; font-size: 15px; font-weight: 500;"
                    +title
                }
                span {
                    style = "color: ${t.proBadge}; font-size: 13px; font-weight: 500; cursor: pointer;"
                    +StringKey.PRICING_UPGRADE_TO_PRO.tr()
                }
            }
            if (subtitle.isNotEmpty()) {
                div {
                    style = "color: ${t.textSecondary}; font-size: 13px; margin-top: 2px;"
                    +subtitle
                }
            }
        }
    }

    // --- Context support ---

    private fun hexToRgba(hex: String, alpha: Double): String {
        val clean = hex.removePrefix("#")
        val r = clean.substring(0, 2).toInt(16)
        val g = clean.substring(2, 4).toInt(16)
        val b = clean.substring(4, 6).toInt(16)
        return "rgba($r, $g, $b, $alpha)"
    }

    /**
     * Load contexts from server, render the action card (if user has contexts),
     * and render context chips in the details box.
     */
    private suspend fun loadAndRenderContexts(rootElement: HTMLElement) {
        try {
            val api = apiService
            if (api == null) return
            val body = api.getContexts()
            val contexts = body.contexts.map { it.toTransactionContext() }
            if (contexts.isEmpty()) return

            // Render context chips in details box
            renderContextChips(rootElement, contexts)

            // Render context action card
            renderContextActionCard(rootElement, contexts)
        } catch (e: Exception) {
            // Context loading failed silently
        }
    }

    private fun renderContextChips(rootElement: HTMLElement, contexts: List<TransactionContext>) {
        val row = rootElement.querySelector("#context-display-row") as? HTMLElement
        if (row == null) return
        val container = rootElement.querySelector("#context-chips-container") as? HTMLElement ?: return
        val assignedIds = transaction.contextIds
        if (assignedIds.isEmpty()) {
            row.style.display = "none"
            return
        }
        row.style.display = "flex"
        container.innerHTML = ""
        assignedIds.forEach { ctxId ->
            val ctx = contexts.find { it.id == ctxId } ?: return@forEach
            val chip = document.createElement("span") as HTMLElement
            chip.textContent = ctx.name
            chip.style.cssText = """
                font-size: 11px;
                padding: 2px 8px;
                border-radius: 12px;
                color: ${ctx.color};
                background: ${hexToRgba(ctx.color, 0.15)};
                border: 1px solid ${hexToRgba(ctx.color, 0.4)};
                font-weight: 500;
                white-space: nowrap;
            """
            container.appendChild(chip)
        }
    }

    private fun renderContextActionCard(rootElement: HTMLElement, contexts: List<TransactionContext>) {
        val t = WebTheme.current
        val placeholder = rootElement.querySelector("#context-action-placeholder") as? HTMLElement ?: return
        placeholder.innerHTML = ""

        val isMobile = ResponsiveUtils.isMobile()
        val cardMargin = if (isMobile) "8px" else "0"
        val heightRule = if (isMobile) "" else "height: 100%; box-sizing: border-box;"

        val card = document.create.div {
            if (isProTier || isTrialing) {
                // Clickable card
                style = """
                    background: ${t.card};
                    border-radius: 16px;
                    padding: 14px 20px;
                    margin-bottom: $cardMargin;
                    cursor: pointer;
                    transition: background 0.15s ease;
                    $heightRule
                """
                onMouseOver = "this.style.backgroundColor='${t.iconCircleDefault}'"
                onMouseOut = "this.style.backgroundColor='${t.card}'"
                onClickFunction = { showContextModal(rootElement, contexts) }
                div {
                    style = "display: flex; align-items: center; gap: 8px;"
                    span {
                        style = "color: ${t.textPrimary}; font-size: 15px; font-weight: 500;"
                        +StringKey.CONTEXT_ACTION_TITLE.tr()
                    }
                    // Trialing users see the "Pro" pill here (showProTag is true for
                    // them in this pro-or-trialing branch); a permanent Pro sees
                    // none. Standard users get the locked upsell card (else branch).
                    if (showProTag) {
                        span {
                            style = """
                                font-size: 9px; font-weight: 700; color: ${t.proBadge}; background: rgba(245, 158, 11, 0.15);
                                padding: 2px 6px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.5px;
                            """
                            +"Pro"
                        }
                    }
                }
                div {
                    style = "color: ${t.textSecondary}; font-size: 13px; margin-top: 2px;"
                    +StringKey.CONTEXT_ACTION_SUBTITLE.tr()
                }
            } else {
                // Locked card
                style = """
                    background: ${t.card};
                    border-radius: 16px;
                    padding: 14px 20px;
                    margin-bottom: $cardMargin;
                    cursor: pointer;
                    transition: background 0.15s ease;
                    opacity: 0.7;
                    $heightRule
                """
                onMouseOver = "this.style.backgroundColor='${t.iconCircleDefault}'"
                onMouseOut = "this.style.backgroundColor='${t.card}'"
                onClickFunction = { onSubscribeClick?.invoke() }
                div {
                    style = "display: flex; align-items: center; justify-content: space-between;"
                    span {
                        style = "color: ${t.textSecondary}; font-size: 15px; font-weight: 500;"
                        +StringKey.CONTEXT_ACTION_TITLE.tr()
                    }
                    span {
                        style = "color: ${t.proBadge}; font-size: 13px; font-weight: 500; cursor: pointer;"
                        +StringKey.PRICING_UPGRADE_TO_PRO.tr()
                    }
                }
                div {
                    style = "color: ${t.textSecondary}; font-size: 13px; margin-top: 2px;"
                    +StringKey.CONTEXT_ACTION_SUBTITLE.tr()
                }
            }
        }
        // On desktop the placeholder sits in a grid cell and needs to stretch
        // so its child card can fill the row height.
        if (!isMobile) {
            placeholder.style.height = "100%"
        }
        placeholder.appendChild(card)
    }

    private fun showContextModal(rootElement: HTMLElement, contexts: List<TransactionContext>) {
        val t = WebTheme.current
        // Build a set of currently assigned context ids
        val selectedIds = transaction.contextIds.toMutableSet()

        // Create overlay
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; background: rgba(0,0,0,0.6);
            display: flex; align-items: center; justify-content: center;
            z-index: 10000;
        """
        overlay.addEventListener("click", { e ->
            if (e.target == overlay) overlay.remove()
        })

        val modal = document.create.div {
            style = """
                background: ${t.card}; border-radius: 20px;
                padding: 24px; width: 340px; max-height: 80vh;
                overflow-y: auto; box-shadow: 0 8px 32px rgba(0,0,0,0.5);
            """
            onClickFunction = { it.stopPropagation() }

            // Title
            div {
                style = "font-size: 17px; font-weight: 600; color: ${t.textPrimary}; margin-bottom: 4px;"
                +StringKey.CONTEXT_ASSIGN_TITLE.tr()
            }
            div {
                style = "font-size: 13px; color: ${t.textSecondary}; margin-bottom: 16px;"
                +StringKey.CONTEXT_ASSIGN_SUBTITLE.tr()
            }

            // Context rows
            contexts.forEach { ctx ->
                div {
                    style = """
                        display: flex; align-items: center; gap: 12px;
                        padding: 10px 0; border-bottom: 1px solid ${t.border};
                        cursor: pointer;
                    """
                    val rowId = "ctx-check-${ctx.id}"
                    onClickFunction = { _ ->
                        val cb = document.getElementById(rowId) as? HTMLInputElement
                        if (cb != null) {
                            cb.checked = !cb.checked
                            if (cb.checked) selectedIds.add(ctx.id) else selectedIds.remove(ctx.id)
                        }
                    }

                    // Checkbox
                    checkBoxInput {
                        id = rowId
                        checked = ctx.id in selectedIds
                        style = "accent-color: ${ctx.color}; width: 18px; height: 18px; flex-shrink: 0; cursor: pointer;"
                        onClickFunction = { e ->
                            e.stopPropagation()
                            val cb = e.target as? HTMLInputElement
                            if (cb != null) {
                                if (cb.checked) selectedIds.add(ctx.id) else selectedIds.remove(ctx.id)
                            }
                        }
                    }

                    // Color dot
                    span {
                        style = """
                            width: 8px; height: 8px; border-radius: 50%;
                            background: ${ctx.color}; flex-shrink: 0;
                        """
                    }

                    // Name
                    span {
                        style = "color: ${t.textPrimary}; font-size: 15px;"
                        +ctx.name
                    }
                }
            }

            // Done button
            button {
                id = "context-modal-done-btn"
                style = """
                    width: 100%; margin-top: 16px; padding: 12px;
                    background: ${t.accent}; color: ${if (t.isDark) "#000" else "#fff"}; border: none;
                    border-radius: 12px; font-size: 15px; font-weight: 600;
                    cursor: pointer;
                """
                +"Done"
            }
        }

        overlay.appendChild(modal)
        document.body?.appendChild(overlay)

        // Wire up Done button
        val doneBtn = modal.querySelector("#context-modal-done-btn") as? HTMLElement
        doneBtn?.addEventListener("click", {
            overlay.remove()
            MainScope().launch {
                saveTransactionContexts(rootElement, selectedIds.toList(), contexts)
            }
        })
    }

    private suspend fun saveTransactionContexts(
        rootElement: HTMLElement,
        contextIds: List<String>,
        allContexts: List<TransactionContext>
    ) {
        try {
            val api = apiService ?: return
            api.setTransactionContexts(transaction.id, contextIds)
            // Update local display and notify parent to refresh transaction data
            updateContextChipsAfterSave(rootElement, contextIds, allContexts)
            onContextChanged?.invoke()
        } catch (e: Exception) {
            console.log("Failed to save transaction contexts: ${e.message}")
        }
    }

    private fun updateContextChipsAfterSave(
        rootElement: HTMLElement,
        contextIds: List<String>,
        allContexts: List<TransactionContext>
    ) {
        val row = rootElement.querySelector("#context-display-row") as? HTMLElement ?: return
        val container = rootElement.querySelector("#context-chips-container") as? HTMLElement ?: return
        if (contextIds.isEmpty()) {
            row.style.display = "none"
            return
        }
        row.style.display = "flex"
        container.innerHTML = ""
        contextIds.forEach { ctxId ->
            val ctx = allContexts.find { it.id == ctxId } ?: return@forEach
            val chip = document.createElement("span") as HTMLElement
            chip.textContent = ctx.name
            chip.style.cssText = """
                font-size: 11px;
                padding: 2px 8px;
                border-radius: 12px;
                color: ${ctx.color};
                background: ${hexToRgba(ctx.color, 0.15)};
                border: 1px solid ${hexToRgba(ctx.color, 0.4)};
                font-weight: 500;
                white-space: nowrap;
            """
            container.appendChild(chip)
        }
    }

    private fun ContextApiModel.toTransactionContext() = TransactionContext(
        id = id,
        userId = user_id,
        name = name,
        color = color,
        createdAt = created_at,
        updatedAt = updated_at
    )

    /**
     * Opens a centered overlay modal listing the user's active goals so the
     * user can assign this transaction to a goal (or remove an existing
     * assignment). Replaces the old inline-expand behaviour which didn't fit
     * the web desktop grid layout.
     */
    private fun showGoalSelectorModal() {
        val t = WebTheme.current

        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; background: rgba(0,0,0,0.6);
            display: flex; align-items: center; justify-content: center;
            z-index: 10000;
        """
        overlay.addEventListener("click", { e ->
            if (e.target == overlay) overlay.remove()
        })

        val modal = document.createElement("div") as HTMLElement
        modal.style.cssText = """
            background: ${t.card}; border-radius: 20px;
            padding: 24px; width: 360px; max-width: 90vw; max-height: 80vh;
            overflow-y: auto; box-shadow: 0 8px 32px rgba(0,0,0,0.5);
            box-sizing: border-box;
        """
        modal.addEventListener("click", { it.stopPropagation() })

        // Title + subtitle mirror the card header copy
        val title = document.createElement("div") as HTMLElement
        title.setAttribute("style", "font-size: 17px; font-weight: 600; color: ${t.textPrimary}; margin-bottom: 4px;")
        title.textContent = StringKey.TRANSACTION_ASSIGN_GOAL.tr()
        modal.appendChild(title)

        val subtitle = document.createElement("div") as HTMLElement
        subtitle.setAttribute("style", "font-size: 13px; color: ${t.textSecondary}; margin-bottom: 16px;")
        subtitle.textContent = StringKey.TRANSACTION_ASSIGN_GOAL_DESC.tr()
        modal.appendChild(subtitle)

        val content = document.createElement("div") as HTMLElement
        content.setAttribute("style", "display: block;")
        val loading = document.createElement("div") as HTMLElement
        loading.setAttribute("style", "color: ${t.textSecondary}; font-size: 14px; padding: 8px 0;")
        loading.textContent = "Loading goals..."
        content.appendChild(loading)
        modal.appendChild(content)

        overlay.appendChild(modal)
        document.body?.appendChild(overlay)

        val onClose: () -> Unit = { overlay.remove() }

        if (goalsRepository != null) {
            MainScope().launch {
                try {
                    val userId = auth.UserIdManager().getUserId()
                    val result = goalsRepository.getAllGoals(userId)
                    if (result is repository.DatabaseResult.Success) {
                        renderGoalsList(content, result.data.filter { it.isActive }, onClose)
                    } else {
                        loading.textContent = "Failed to load goals."
                    }
                } catch (e: Exception) {
                    loading.textContent = "Failed to load goals: ${e.message}"
                }
            }
        }
    }

    /**
     * Phase 2 entry point for deleting a user-created subcategory.
     *
     * Checks usage (via /api/categories/custom/{id}/usage) and decides:
     *   - Silently deletes when unused AND deleting it doesn't cascade the
     *     primary category away.
     *   - Otherwise shows a confirmation modal with an optional replacement
     *     picker and the list of affected transactions (and, if the primary
     *     itself will go away, a note about that).
     *
     * @param onDeleted called after a successful delete so the caller can
     *   refresh local state and re-render.
     */
    // Patch the detail view's category pill + icon with a fresh Plaid-style code.
    // Used right after a custom-category delete when the server tells us the
    // current transaction got re-categorized — saves a detail-view refetch.
    private fun applyCategoryCodeToDetailUI(newCode: String) {
        val parts = newCode.split("/")
        val displayText = if (parts.size >= 2) {
            utils.CategoryUtils.formatTwoLevelCategoryLocalized(parts[0], parts[1])
        } else {
            utils.CategoryUtils.formatCategoryNameLocalized(newCode)
        }
        document.getElementById("txn-detail-category-display")?.textContent = displayText
        // User-override map is needed so custom primaries map to their chosen colour
        // instead of falling through to the default grey.
        val newColor = utils.CategoryUtils.getCategoryColor(newCode, userCategoryColors)
        val newIconId = utils.CategoryUtils.getCategoryIconId(newCode)
        val userIconSvg = findCustomIconId(
            initialCustomCategories,
            parts.getOrNull(0).orEmpty(),
            parts.getOrNull(1),
        )?.let { utils.CustomCategoryIcons.renderSvg(it, size = 22) }
        (document.getElementById("txn-detail-icon") as? HTMLElement)?.let { iconEl ->
            iconEl.style.borderColor = newColor
            iconEl.style.color = WebTheme.current.iconStroke
            iconEl.innerHTML = userIconSvg ?: CategoryIconSvg.render(newIconId, size = 22)
        }
    }

    private fun handleDeleteCustomCategory(
        custom: CustomCategoryData,
        allCustom: List<CustomCategoryData>,
        isUserCreatedPrimary: Boolean,
        onDeleted: () -> Unit,
    ) {
        val remainingUserSubs = allCustom.count {
            it.primaryName.equals(custom.primaryName, ignoreCase = true) && it.id != custom.id
        }
        val willCascadePrimary = isUserCreatedPrimary && remainingUserSubs == 0
        val api = apiService ?: return

        MainScope().launch {
            val usage = try { api.getCustomCategoryUsage(custom.id) } catch (_: Exception) { null }
            val count = usage?.count ?: 0L

            if (count == 0L && !willCascadePrimary) {
                try {
                    api.deleteCustomCategory(custom.id)
                    onDeleted()
                } catch (e: Exception) {
                    console.error("Delete failed: ${e.message}")
                }
                return@launch
            }

            showDeleteCustomCategoryConfirm(
                custom = custom,
                usage = usage,
                willCascadePrimary = willCascadePrimary,
                allCustom = allCustom,
                onConfirmed = { reassignPri, reassignSec ->
                    MainScope().launch {
                        try {
                            val res = api.deleteCustomCategory(custom.id, reassignPri, reassignSec)
                            // Patch the detail view's category cell + icon in place so the
                            // user stays where they are and sees the new category immediately.
                            res.updates[transaction.id]?.let { newCode ->
                                applyCategoryCodeToDetailUI(newCode)
                            }
                            onDeleted()
                        } catch (e: Exception) {
                            console.error("Delete failed: ${e.message}")
                        }
                    }
                },
            )
        }
    }

    private fun showDeleteCustomCategoryConfirm(
        custom: CustomCategoryData,
        usage: CustomCategoryUsageResponse?,
        willCascadePrimary: Boolean,
        allCustom: List<CustomCategoryData>,
        onConfirmed: (String?, String?) -> Unit,
    ) {
        val t = WebTheme.current
        val count = usage?.count ?: 0L
        val sample = usage?.sample.orEmpty()

        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; background: rgba(0,0,0,0.65);
            display: flex; align-items: center; justify-content: center;
            z-index: 11000;
        """.trimIndent()
        overlay.addEventListener("click", { e -> if (e.target == overlay) overlay.remove() })

        val modal = document.createElement("div") as HTMLElement
        modal.style.cssText = """
            background: ${t.card}; border-radius: 16px;
            padding: 24px; width: 480px; max-width: 92vw; max-height: 85vh;
            overflow-y: auto; color: ${t.textPrimary}; box-sizing: border-box;
        """.trimIndent()
        modal.addEventListener("click", { it.stopPropagation() })

        val title = document.createElement("h3") as HTMLElement
        title.setAttribute("style", "margin: 0 0 8px 0; font-size: 18px; font-weight: 600;")
        title.textContent = StringKey.CATEGORY_DELETE_TITLE.tr().replace("%s", custom.secondaryName)
        modal.appendChild(title)

        val summary = document.createElement("p") as HTMLElement
        summary.setAttribute("style", "margin: 0 0 16px 0; color: ${t.textSecondary}; font-size: 14px;")
        summary.textContent = when {
            count == 0L -> StringKey.CATEGORY_DELETE_USAGE_NONE.tr()
            count == 1L -> StringKey.CATEGORY_DELETE_USAGE_ONE.tr()
            else -> StringKey.CATEGORY_DELETE_USAGE_MANY.tr().replace("%s", count.toString())
        }
        modal.appendChild(summary)

        // Replacement picker (only when there's actual usage to move).
        // Two-level drill: pick a primary, then pick one of its subs.
        // Null pick ("Remove override") clears user_category so the AI/Plaid
        // default takes over; any other pick writes primary+sub into both
        // user_category and personal_finance_* (see server reassignUserCategory).
        var reassignToPrimary: String? = null
        var reassignToSecondary: String? = null
        if (count > 0L) {
            val label = document.createElement("p") as HTMLElement
            label.setAttribute("style", "color: ${t.textSecondary}; font-size: 13px; margin: 0 0 6px 0;")
            label.textContent = StringKey.CATEGORY_DELETE_MOVE_TO.tr()
            modal.appendChild(label)

            val removeLabel = StringKey.CATEGORY_DELETE_REMOVE_OVERRIDE.tr()

            // Drill-down panel (shows either primary list or sub list).
            val panel = document.createElement("div") as HTMLElement
            panel.setAttribute("style", """
                max-height: 280px; overflow-y: auto;
                background: ${t.background}; border: 1px solid ${t.border};
                border-radius: 8px; margin-bottom: 16px;
            """.trimIndent())
            modal.appendChild(panel)

            // Build merged primary list: Plaid primaries + user-created primaries.
            // When a custom sub lives under a primary name that also exists in Plaid,
            // we merge its subs into the Plaid primary's list so the user sees one
            // entry per primary.
            // Triple<subDisplayName, storedPrimary, storedSecondary>:
            //   storedPrimary/Secondary is what we send to the server — Plaid codes
            //   for built-in subs, display names for custom subs (same convention
            //   the rest of the category picker uses).
            val plaidPrimaries = getSelectableCategories(isDebit = !custom.isIncome)
                .filter { it.plaidCode != "__DIVIDER__" }
            val otherCustoms = allCustom
                .filter { it.id != custom.id && it.isIncome == custom.isIncome }
            val customByLowerPrimary = otherCustoms.groupBy { it.primaryName.lowercase() }

            val primaryRows = mutableListOf<Pair<String, List<Triple<String, String, String>>>>()
            val usedLowerNames = mutableSetOf<String>()
            plaidPrimaries.forEach { p ->
                val lower = p.name.lowercase()
                usedLowerNames += lower
                val plaidSubs = p.subcategories.map { sub ->
                    Triple(sub.name, p.plaidCode, sub.plaidCode)
                }
                val customSubs = customByLowerPrimary[lower].orEmpty().map { s ->
                    Triple(s.secondaryName, s.primaryName, s.secondaryName)
                }
                primaryRows += p.name to (plaidSubs + customSubs)
            }
            customByLowerPrimary.forEach { (lower, list) ->
                if (lower !in usedLowerNames) {
                    primaryRows += list.first().primaryName to list.map { s ->
                        Triple(s.secondaryName, s.primaryName, s.secondaryName)
                    }
                }
            }
            primaryRows.sortBy { it.first.lowercase() }

            // State: which primary is drilled into (null => top-level primary list).
            var drillInto: String? = null

            // Fixed-width leading slot so rows stay aligned whether or not a ✓ is shown.
            fun leadingSlot(showCheck: Boolean): HTMLElement {
                val s = document.createElement("span") as HTMLElement
                s.setAttribute("style", "display: inline-flex; width: 16px; justify-content: center; color: ${t.textPrimary};")
                if (showCheck) s.textContent = "✓"
                return s
            }

            fun renderPanel() {
                panel.innerHTML = ""
                val removeSelected = reassignToPrimary == null && reassignToSecondary == null
                val current = drillInto
                if (current == null) {
                    // "Remove override" row (always first).
                    val rm = document.createElement("div") as HTMLElement
                    rm.setAttribute("style", """
                        display: flex; align-items: center; gap: 10px;
                        padding: 10px 12px; cursor: pointer;
                        color: ${t.textSecondary}; font-size: 14px;
                        border-bottom: 1px solid ${t.border};
                    """.trimIndent())
                    rm.appendChild(leadingSlot(removeSelected))
                    val rmText = document.createElement("span") as HTMLElement
                    rmText.setAttribute("style", "flex: 1;")
                    rmText.textContent = removeLabel
                    rm.appendChild(rmText)
                    rm.addEventListener("mouseover", { rm.style.background = "rgba(128,128,128,0.12)" })
                    rm.addEventListener("mouseout", { rm.style.background = "transparent" })
                    rm.addEventListener("click", {
                        reassignToPrimary = null
                        reassignToSecondary = null
                        renderPanel()
                    })
                    panel.appendChild(rm)

                    primaryRows.forEach { (primaryName, subs) ->
                        val isHere = subs.any { it.second == reassignToPrimary && it.third == reassignToSecondary }
                        val r = document.createElement("div") as HTMLElement
                        r.setAttribute("style", """
                            display: flex; align-items: center; gap: 10px;
                            padding: 10px 12px; cursor: pointer;
                            color: ${t.textPrimary}; font-size: 14px;
                            border-bottom: 1px solid ${t.border};
                        """.trimIndent())
                        r.appendChild(leadingSlot(isHere))
                        val textSpan = document.createElement("span") as HTMLElement
                        textSpan.setAttribute("style", "flex: 1;")
                        textSpan.textContent = primaryName
                        r.appendChild(textSpan)
                        val chev = document.createElement("span") as HTMLElement
                        chev.setAttribute("style", "color: ${t.textSecondary}; font-size: 16px;")
                        chev.textContent = "›"
                        r.appendChild(chev)
                        r.addEventListener("mouseover", { r.style.background = "rgba(128,128,128,0.12)" })
                        r.addEventListener("mouseout", { r.style.background = "transparent" })
                        r.addEventListener("click", {
                            drillInto = primaryName
                            renderPanel()
                        })
                        panel.appendChild(r)
                    }
                } else {
                    val subs = primaryRows.firstOrNull { it.first == current }?.second.orEmpty()

                    val back = document.createElement("div") as HTMLElement
                    back.setAttribute("style", """
                        display: flex; align-items: center; gap: 8px;
                        padding: 10px 12px; cursor: pointer;
                        font-size: 13px; border-bottom: 1px solid ${t.border};
                        background: rgba(128,128,128,0.05);
                    """.trimIndent())
                    val chevL = document.createElement("span") as HTMLElement
                    chevL.setAttribute("style", "font-size: 16px; color: ${t.textSecondary};")
                    chevL.textContent = "‹"
                    back.appendChild(chevL)
                    val primarySpan = document.createElement("span") as HTMLElement
                    primarySpan.setAttribute("style", "flex: 1; font-weight: 500; color: ${t.textPrimary};")
                    primarySpan.textContent = current
                    back.appendChild(primarySpan)
                    back.addEventListener("click", {
                        drillInto = null
                        renderPanel()
                    })
                    panel.appendChild(back)

                    subs.forEach { (subName, storedPrimary, storedSecondary) ->
                        val isSel = storedPrimary == reassignToPrimary && storedSecondary == reassignToSecondary
                        val r = document.createElement("div") as HTMLElement
                        r.setAttribute("style", """
                            display: flex; align-items: center; gap: 10px;
                            padding: 10px 12px; cursor: pointer;
                            color: ${t.textPrimary}; font-size: 14px;
                            border-bottom: 1px solid ${t.border};
                        """.trimIndent())
                        r.appendChild(leadingSlot(isSel))
                        val textSpan = document.createElement("span") as HTMLElement
                        textSpan.setAttribute("style", "flex: 1;")
                        textSpan.textContent = subName
                        r.appendChild(textSpan)
                        r.addEventListener("mouseover", { r.style.background = "rgba(128,128,128,0.12)" })
                        r.addEventListener("mouseout", { r.style.background = "transparent" })
                        r.addEventListener("click", {
                            reassignToPrimary = storedPrimary
                            reassignToSecondary = storedSecondary
                            drillInto = null
                            renderPanel()
                        })
                        panel.appendChild(r)
                    }
                }
            }
            renderPanel()
        }

        if (sample.isNotEmpty()) {
            val sh = document.createElement("p") as HTMLElement
            sh.setAttribute("style", "color: ${t.textSecondary}; font-size: 12px; margin: 8px 0 6px 0;")
            sh.textContent = if (count > sample.size.toLong())
                StringKey.CATEGORY_DELETE_AFFECTED_HEADER_COUNT.tr()
                    .replaceFirst("%s", sample.size.toString())
                    .replaceFirst("%s", count.toString())
            else
                StringKey.CATEGORY_DELETE_AFFECTED_HEADER.tr()
            modal.appendChild(sh)

            val list = document.createElement("div") as HTMLElement
            list.setAttribute("style", """
                max-height: 200px; overflow-y: auto;
                background: ${t.background}; border: 1px solid ${t.border};
                border-radius: 8px; padding: 8px 12px; margin-bottom: 16px;
                font-size: 13px;
            """.trimIndent())
            sample.forEach { s ->
                val row = document.createElement("div") as HTMLElement
                row.setAttribute("style", "display: flex; justify-content: space-between; gap: 8px; padding: 4px 0; color: ${t.textPrimary};")
                val left = document.createElement("span") as HTMLElement
                left.setAttribute("style", "flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;")
                left.textContent = "${s.date}  ${s.merchant}"
                val right = document.createElement("span") as HTMLElement
                right.setAttribute("style", "color: ${t.textSecondary};")
                val abs = kotlin.math.abs(s.amount).asDynamic().toFixed(2)
                right.textContent = "${'$'}$abs"
                row.appendChild(left)
                row.appendChild(right)
                list.appendChild(row)
            }
            modal.appendChild(list)
        }

        if (willCascadePrimary) {
            val note = document.createElement("p") as HTMLElement
            note.setAttribute("style", """
                color: ${t.targetOrange}; font-size: 13px; margin: 0 0 16px 0;
                padding: 10px 12px; background: rgba(245,158,11,0.08);
                border-radius: 8px; border-left: 3px solid ${t.targetOrange};
            """.trimIndent())
            note.textContent = StringKey.CATEGORY_DELETE_CASCADE_NOTE.tr().replace("%s", custom.primaryName)
            modal.appendChild(note)
        }

        val btnRow = document.createElement("div") as HTMLElement
        btnRow.setAttribute("style", "display: flex; gap: 8px; justify-content: flex-end;")
        val cancel = document.createElement("button") as org.w3c.dom.HTMLButtonElement
        cancel.setAttribute("style", "padding: 10px 18px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 14px; cursor: pointer;")
        cancel.textContent = StringKey.ACTION_CANCEL.tr()
        cancel.addEventListener("click", { overlay.remove() })
        btnRow.appendChild(cancel)

        val del = document.createElement("button") as org.w3c.dom.HTMLButtonElement
        del.setAttribute("style", "padding: 10px 18px; background: ${t.expenseRed}; border: none; border-radius: 8px; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer;")
        del.textContent = StringKey.ACTION_DELETE.tr()
        del.addEventListener("click", {
            overlay.remove()
            onConfirmed(reassignToPrimary, reassignToSecondary)
        })
        btnRow.appendChild(del)
        modal.appendChild(btnRow)

        overlay.appendChild(modal)
        document.body?.appendChild(overlay)
    }

    private fun renderGoalsList(
        content: HTMLElement,
        goals: List<models.SavingsGoal>,
        onDone: (() -> Unit)? = null
    ) {
        val t = WebTheme.current
        content.innerHTML = ""

        // "None" button to remove goal assignment — created manually with addEventListener
        if (transaction.assignedGoalId != null && goals.isNotEmpty()) {
            val noneRow = document.createElement("div") as HTMLElement
            noneRow.setAttribute("style", "display:flex;align-items:center;gap:12px;padding:10px 0;cursor:pointer;border-bottom:1px solid ${t.border};")
            noneRow.asDynamic().innerHTML = """
                <span style="width:20px;height:20px;display:flex;align-items:center;justify-content:center;background:${t.border};border-radius:50%;font-size:12px;color:${t.textSecondary};">✕</span>
                <span style="font-weight:500;color:${t.expenseRed};font-size:14px;">${StringKey.TRANSACTION_REMOVE_GOAL.tr()}</span>
            """.trimIndent()
            noneRow.addEventListener("click", { evt ->
                evt.stopPropagation()
                if (apiService != null) {
                    MainScope().launch {
                        try {
                            apiService.removeGoalAssignment(transaction.id)
                            val goalDisplay = document.getElementById("goal-assignment-display") as? HTMLElement
                            if (goalDisplay != null) goalDisplay.style.display = "none"
                            onGoalRemoved?.invoke()
                            onDone?.invoke()
                        } catch (e: Exception) {
                            console.error("Failed to remove goal: ${e.message}")
                        }
                    }
                }
            })
            content.appendChild(noneRow)
        }

        val listDiv = document.create.div {
            if (goals.isEmpty()) {
                div {
                    style = "padding: 4px 0;"
                    span {
                        style = "color: #00d9ff; font-size: 14px; cursor: pointer;"
                        onClickFunction = {
                            if (goalsRepository != null) {
                                val body = document.body
                                if (body != null) {
                                    // Build a transient ViewModel around this transaction-detail's
                                    // SavingsGoalsRepository so the type picker can drive the
                                    // unified create flow (Savings vs Debt Studio in create mode).
                                    val transientVm = WebSavingsGoalsViewModel(repository = goalsRepository)
                                    WebGoalDialogHelper.showGoalTypePicker(body, transientVm) {
                                        // After successful create, refresh the goal-assignment list
                                        // in this modal so the new goal is selectable immediately.
                                        MainScope().launch {
                                            val userId = auth.UserIdManager().getUserId()
                                            val result = goalsRepository.getActiveGoals(userId)
                                            if (result is repository.DatabaseResult.Success) {
                                                renderGoalsList(content, result.data, onDone)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        +StringKey.TRANSACTION_GOAL_CREATE.tr()
                    }
                }
            } else {

                goals.forEach { goal ->
                    val isAssigned = goal.id == transaction.assignedGoalId
                    val progress = if (goal.targetAmount > 0) (goal.currentAmount / goal.targetAmount).coerceIn(0.0, 1.0) else 0.0
                    val remaining = goal.targetAmount - goal.currentAmount
                    div {
                        style = """
                            display: flex;
                            align-items: center;
                            gap: 12px;
                            padding: 10px 0;
                            cursor: pointer;
                            border-bottom: 1px solid ${t.border};
                        """
                        onClickFunction = {
                            if (apiService != null) {
                                MainScope().launch {
                                    try {
                                        apiService.assignTransactionToGoal(
                                            transactionId = transaction.id,
                                            goalId = goal.id,
                                            amount = kotlin.math.abs(transaction.amount)
                                        )
                                        // Update the goal badge in the header immediately
                                        val goalDisplay = document.getElementById("goal-assignment-display") as? HTMLElement
                                        if (goalDisplay != null) {
                                            val iconHtml = CategoryIconSvg.renderGoalIcon(goal.iconEmoji, size = 14, color = "#66A3FF")
                                            goalDisplay.style.display = "flex"
                                            goalDisplay.style.alignItems = "center"
                                            goalDisplay.innerHTML = """
                                                <div style="font-size: 13px; color: ${t.textSecondary}; width: 70px; flex-shrink: 0;">${StringKey.TRANSACTION_GOAL.tr()}</div>
                                                <div style="display: flex; align-items: center; gap: 4px;">
                                                    <span style="font-size: 14px;">$iconHtml</span>
                                                    <span style="font-size: 14px; font-weight: 500; color: #66A3FF;">${goal.name}</span>
                                                </div>
                                            """.trimIndent()
                                        }
                                        onGoalAssigned?.invoke(goal)
                                        onDone?.invoke()
                                    } catch (e: Exception) {
                                        console.error("Failed to assign goal: ${e.message}")
                                    }
                                }
                            }
                        }
                        // Goal icon (SVG if known, emoji fallback)
                        span {
                            style = "font-size: 20px; display: flex; align-items: center;"
                            unsafe { +CategoryIconSvg.renderGoalIcon(goal.iconEmoji, size = 20, color = t.iconStroke) }
                        }
                        // Goal info
                        div {
                            style = "flex: 1;"
                            div {
                                style = "font-weight: 500; color: ${t.textPrimary}; font-size: 14px;"
                                +goal.name
                            }
                            div {
                                style = "font-size: 12px; color: ${t.textSecondary};"
                                val absRemaining = if (remaining < 0) -remaining else remaining
                                +"$${absRemaining.asDynamic().toFixed(0)} ${StringKey.TRANSACTION_GOAL_REMAINING.tr()}"
                            }
                        }
                        // Checkmark for currently assigned goal
                        if (isAssigned) {
                            span {
                                style = "color: #4CAF50; font-size: 16px; font-weight: bold; flex-shrink: 0;"
                                +"✓"
                            }
                        }
                        // Circular progress indicator (SVG)
                        val pct = (progress * 100).toInt()
                        val progressColor = if (progress >= 1.0) "#10b981" else "#00d9ff"
                        val r = 14
                        val sw = 3
                        val circ = (2 * 3.14159 * r * 100).toInt() / 100.0
                        val dashOffset = (circ * (1 - progress) * 100).toInt() / 100.0
                        span {
                            style = "flex-shrink: 0;"
                            unsafe {
                                +"""<svg width="36" height="36" viewBox="0 0 36 36">
                                    <circle cx="18" cy="18" r="$r" fill="none" stroke="#333" stroke-width="$sw"/>
                                    <circle cx="18" cy="18" r="$r" fill="none" stroke="$progressColor" stroke-width="$sw"
                                        stroke-linecap="round" stroke-dasharray="$circ" stroke-dashoffset="$dashOffset"
                                        transform="rotate(-90 18 18)"/>
                                    <text x="18" y="18" text-anchor="middle" dominant-baseline="central"
                                        fill="#888" font-size="9" font-weight="bold">$pct%</text>
                                </svg>"""
                            }
                        }
                    }
                }
                // "Create a goal" link after the list
                div {
                    style = "padding: 10px 0 4px 0;"
                    span {
                        style = "color: #00d9ff; font-size: 14px; cursor: pointer;"
                        onClickFunction = {
                            if (goalsRepository != null) {
                                val body = document.body
                                if (body != null) {
                                    // Build a transient ViewModel around this transaction-detail's
                                    // SavingsGoalsRepository so the type picker can drive the
                                    // unified create flow (Savings vs Debt Studio in create mode).
                                    val transientVm = WebSavingsGoalsViewModel(repository = goalsRepository)
                                    WebGoalDialogHelper.showGoalTypePicker(body, transientVm) {
                                        // After successful create, refresh the goal-assignment list
                                        // in this modal so the new goal is selectable immediately.
                                        MainScope().launch {
                                            val userId = auth.UserIdManager().getUserId()
                                            val result = goalsRepository.getActiveGoals(userId)
                                            if (result is repository.DatabaseResult.Success) {
                                                renderGoalsList(content, result.data, onDone)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        +StringKey.TRANSACTION_GOAL_CREATE.tr()
                    }
                }
            }
        }
        content.appendChild(listDiv)
    }

    private fun DIV.createActionButton(title: String, onClick: () -> Unit) {
        val t = WebTheme.current
        button {
            style = """
                width: 100%;
                padding: 12px 16px;
                background: ${t.iconCircleDefault};
                border: 1px solid ${t.accent};
                border-radius: 8px;
                color: ${t.accent};
                font-size: 16px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                text-align: center;
            """
            onMouseOver = "this.style.backgroundColor='${t.accent}'; this.style.color='${if (t.isDark) "#000" else "#fff"}'"
            onMouseOut = "this.style.backgroundColor='${t.iconCircleDefault}'; this.style.color='${t.accent}'"
            onClickFunction = { onClick() }

            +title
        }
    }

    private fun DIV.createDestructiveButton(title: String, onClick: () -> Unit) {
        val t = WebTheme.current
        button {
            style = """
                width: 100%;
                padding: 12px 16px;
                background: ${t.iconCircleDefault};
                border: 1px solid ${t.expenseRed};
                border-radius: 8px;
                color: ${t.expenseRed};
                font-size: 16px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                text-align: center;
            """
            onMouseOver = "this.style.backgroundColor='${t.expenseRed}'; this.style.color='${t.textPrimary}'"
            onMouseOut = "this.style.backgroundColor='${t.iconCircleDefault}'; this.style.color='${t.expenseRed}'"
            onClickFunction = { onClick() }

            +title
        }
    }

    
    // Use shared CategoryUtils for consistent colors across all platforms
    private fun getCategoryColor(category: String): String =
        utils.CategoryUtils.getCategoryColor(category, userCategoryColors)
    
    private fun formatCurrency(amount: Double): String {
        val absAmount = if (amount < 0) -amount else amount
        val sign = if (amount < 0) "-" else ""
        val formatted = "${sign}$${absAmount.asDynamic().toFixed(2)}"
        return formatted.replace(Regex("(\\d)(?=(\\d{3})+(?!\\d))"), "$1,")
    }
    
    private fun formatFullDate(date: Date): String {
        val months = arrayOf("January", "February", "March", "April", "May", "June",
                           "July", "August", "September", "October", "November", "December")

        // Use local timezone for user-friendly display
        val month = months[date.getMonth()]
        val day = date.getDate()
        val year = date.getFullYear()

        return "$month $day, $year"
    }

    private fun formatTime(date: Date): String {
        // For date-only transactions, show a placeholder time since the actual time is not meaningful
        return "12:00 PM"
    }

    
    private fun showCategorySelector() {
        val t = WebTheme.current
        // Get categories filtered by transaction type (single source of truth from PlaidCategories.kt)
        // For credit/inflow transactions: income categories first, then expense categories (for refunds etc.)
        val filteredCategories = if (transaction.isDebit) {
            getSelectableCategories(true)
        } else {
            val divider = SelectableCategory(name = i18n.StringKey.CATEGORY_EXPENSE_CATEGORIES.tr(), plaidCode = "__DIVIDER__")
            getSelectableCategories(false) + divider + getSelectableCategories(true)
        }

        // State for hierarchical selection - now uses SelectableCategory
        var selectedPrimary: SelectableCategory? = null
        var pendingCategoryCode: String? = null
        // Seed with the host-supplied list so the picker opens with icons/colors ready
        // instead of the blank state we'd see during the async refresh below.
        var customCategories: List<CustomCategoryData> = initialCustomCategories
        var showCreateForm = false
        var createIsIncome = !transaction.isDebit
        var createError: String? = null
        var selectedPrimaryOption = "" // "" = placeholder, "__NEW__" = add new, else = existing name

        // Modify-mode state: rename/delete user-created categories; recolor any category
        var modifyMode = false
        var editingPrimaryName: String? = null           // name of primary in primary-edit overlay
        var addingSubForPrimary: String? = null          // primary name when the add-sub overlay is open
        var newSubName: String = ""
        var newSubIcon: String = utils.CustomCategoryIcons.ids.first()
        var addSubError: String? = null
        var editingSubId: Long? = null                   // id of user sub in edit overlay
        var editSubName: String = ""
        var editSubIcon: String = utils.CustomCategoryIcons.ids.first()
        var editSubError: String? = null
        var editPrimaryError: String? = null
        var addPrimaryName: String = ""
        var addPrimaryFirstSub: String = ""
        var addPrimaryColor: String = "#00D9FF"
        var addPrimaryIcon: String = utils.CustomCategoryIcons.ids.first()
        var addPrimaryError: String? = null
        var showAddPrimaryOverlay = false

        // Helpers for separating Plaid built-ins from user-created entries.
        val plaidPrimaryNames = filteredCategories.filter { it.plaidCode != "__DIVIDER__" }.map { it.name.lowercase() }.toSet()
        fun isPlaidPrimary(name: String): Boolean = name.lowercase() in plaidPrimaryNames
        fun plaidSubcategoriesFor(primaryName: String): Set<String> =
            filteredCategories.firstOrNull { it.name.equals(primaryName, ignoreCase = true) }
                ?.subcategories?.map { it.name.lowercase() }?.toSet().orEmpty()
        fun isUserSubcategory(primaryName: String, subName: String): Boolean {
            val built = plaidSubcategoriesFor(primaryName)
            if (subName.lowercase() in built) return false
            return customCategories.any {
                it.primaryName.equals(primaryName, ignoreCase = true) &&
                    it.secondaryName.equals(subName, ignoreCase = true)
            }
        }
        fun findCustomCategory(primaryName: String, subName: String): CustomCategoryData? =
            customCategories.firstOrNull {
                it.primaryName.equals(primaryName, ignoreCase = true) &&
                    it.secondaryName.equals(subName, ignoreCase = true)
            }

        // Representative icon id for a user-made primary: use the specific sub's icon
        // when one is provided, otherwise fall back to the first sub under that primary.
        // That first sub becomes the primary's visual identity (no separate "primary icon"
        // storage). Returns null when the primary isn't user-made or has no icon set.
        fun findUserIconId(primaryName: String, subName: String? = null): String? {
            if (primaryName.isEmpty()) return null
            val match =
                if (!subName.isNullOrBlank()) {
                    customCategories.firstOrNull {
                        it.primaryName.equals(primaryName, ignoreCase = true) &&
                            it.secondaryName.equals(subName, ignoreCase = true)
                    }
                } else {
                    customCategories.firstOrNull { it.primaryName.equals(primaryName, ignoreCase = true) }
                }
            return match?.icon?.takeIf { it.isNotBlank() }
        }

        // Create modal element first
        val modalElement = document.createElement("div") as HTMLElement
        modalElement.style.cssText = """
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        """
        modalElement.addEventListener("click", { event ->
            if (event.target == modalElement) document.body?.removeChild(modalElement)
        })
        modalElement.addEventListener("keydown", { event ->
            val keyEvent = event as? org.w3c.dom.events.KeyboardEvent ?: return@addEventListener
            if (keyEvent.key == "Enter") {
                event.preventDefault()
                // Click the primary (cyan) button — identified by its background color
                val buttons = modalElement.querySelectorAll("button")
                for (i in 0 until buttons.length) {
                    val btn = buttons.item(i) as? org.w3c.dom.HTMLElement ?: continue
                    val inlineStyle = btn.getAttribute("style") ?: ""
                    if (inlineStyle.contains("00d9ff")) {
                        btn.click()
                        return@addEventListener
                    }
                }
                // Fallback: click first button
                (modalElement.querySelector("button") as? org.w3c.dom.HTMLElement)?.click()
            }
        })
        modalElement.tabIndex = -1

        // Declare functions with lateinit pattern to handle circular reference
        lateinit var updateModalContent: () -> Unit

        updateModalContent = {
            modalElement.innerHTML = ""

            val modalContent = document.create.div {
                style = """
                    background: ${t.card};
                    border-radius: 16px;
                    padding: 24px;
                    max-width: 500px;
                    width: 90%;
                    max-height: 80vh;
                    overflow-y: auto;
                    border: 1px solid ${t.border};
                """

                // Confirmation dialog — shown after category selection
                if (pendingCategoryCode != null) {
                    h3 {
                        style = "font-size: 20px; font-weight: 600; color: ${t.textPrimary}; margin: 0 0 16px 0; text-align: center;"
                        +StringKey.CATEGORY_APPLY_ALL_TITLE.tr()
                    }
                    div {
                        style = "display: flex; flex-direction: column; gap: 10px; margin-top: 20px;"
                        button {
                            style = """
                                width: 100%; padding: 14px; border-radius: 10px; cursor: pointer;
                                font-size: 15px; font-weight: 600;
                                background: ${t.accent}; color: ${if (t.isDark) "#000" else "#fff"}; border: none;
                            """
                            onClickFunction = {
                                val code = pendingCategoryCode!!
                                pendingCategoryCode = null
                                onCategoryChanged?.invoke(code, false, null)
                                document.body?.removeChild(modalElement)
                            }
                            +StringKey.CATEGORY_APPLY_JUST_THIS.tr()
                        }
                        button {
                            style = """
                                width: 100%; padding: 14px; border-radius: 10px; cursor: pointer;
                                font-size: 15px; font-weight: 500;
                                background: ${t.border}; color: ${t.textPrimary}; border: 1px solid ${t.chipUnselectedBorder};
                            """
                            onClickFunction = fun(_: org.w3c.dom.events.Event) {
                                val code = pendingCategoryCode ?: return
                                if (apiService == null) {
                                    pendingCategoryCode = null
                                    onCategoryChanged?.invoke(code, true, null)
                                    document.body?.removeChild(modalElement)
                                    return
                                }
                                // Fetch preview and show list
                                kotlinx.coroutines.MainScope().launch {
                                    try {
                                        val preview = apiService.getCategoryPreview(transaction.id)
                                        showCategoryPreview(modalElement, preview.sourceTransaction, preview.affectedTransactions, code, onCategoryChanged)
                                    } catch (_: Exception) {
                                        // Fallback: apply immediately if preview fails
                                        pendingCategoryCode = null
                                        onCategoryChanged?.invoke(code, true, null)
                                        document.body?.removeChild(modalElement)
                                    }
                                }
                            }
                            +StringKey.CATEGORY_APPLY_ALL.tr()
                        }
                        p {
                            style = "font-size: 12px; color: ${t.textSecondary}; margin: 4px 0 0 0; text-align: center;"
                            +StringKey.CATEGORY_APPLY_ALL_NOTE.tr()
                        }
                    }
                    return@div
                }

                // Create custom category form — early exit
                if (showCreateForm) {
                    h3 {
                        style = "font-size: 20px; font-weight: 600; color: ${t.textPrimary}; margin: 0 0 16px 0; text-align: center;"
                        +"Add Custom Category"
                    }
                    // Primary category — fixed label if pre-filled from subcategory view, otherwise dropdown
                    val isPrimaryLocked = selectedPrimary != null && selectedPrimaryOption == selectedPrimary!!.name
                    if (isPrimaryLocked) {
                        p { style = "color: ${t.textSecondary}; font-size: 13px; margin-bottom: 6px;"; +"Primary category" }
                        div {
                            style = "padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 12px;"
                            +selectedPrimaryOption
                        }
                    } else {
                        p { style = "color: ${t.textSecondary}; font-size: 13px; margin-bottom: 6px;"; +"Primary category" }
                        // Build primary options: Plaid primaries + existing custom primaries
                        val plaidPrimaries = getSelectableCategories(if (createIsIncome) false else true)
                            .filter { it.plaidCode != "__DIVIDER__" }
                            .map { it.name }
                        val customPrimaries2 = customCategories
                            .filter { it.isIncome == createIsIncome }
                            .map { it.primaryName }
                            .distinct()
                        val allPrimaries = (plaidPrimaries + customPrimaries2).distinct().sorted()
                        select {
                            id = "cc-primary-select"
                            style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 12px; box-sizing: border-box; outline: none; cursor: pointer;"
                            onChangeFunction = { event ->
                                selectedPrimaryOption = (event.target.asDynamic().value as? String) ?: ""
                                updateModalContent()
                            }
                            option {
                                value = ""
                                selected = selectedPrimaryOption.isEmpty()
                                disabled = true
                                +"Select a primary category"
                            }
                            allPrimaries.forEach { name ->
                                option {
                                    value = name
                                    selected = selectedPrimaryOption == name
                                    +name
                                }
                            }
                            option {
                                value = "__NEW__"
                                selected = selectedPrimaryOption == "__NEW__"
                                +"+ Add new primary"
                            }
                        }
                        if (selectedPrimaryOption == "__NEW__") {
                            input {
                                id = "cc-primary-input"
                                type = InputType.text
                                placeholder = "New primary name"
                                style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 12px; box-sizing: border-box; outline: none;"
                            }
                        }
                    }
                    p { style = "color: ${t.textSecondary}; font-size: 13px; margin-bottom: 6px;"; +"Secondary category" }
                    input {
                        id = "cc-secondary-input"
                        type = InputType.text
                        placeholder = "e.g. Crypto"
                        style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 16px; box-sizing: border-box; outline: none;"
                    }
                    if (createError != null) {
                        p { style = "color: #ff4444; font-size: 13px; margin-bottom: 12px;"; +(createError ?: "") }
                    }
                    div {
                        style = "display: flex; gap: 8px;"
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.accent}; border: none; border-radius: 8px; color: ${if (t.isDark) "#000" else "#fff"}; font-size: 15px; font-weight: 700; cursor: pointer;"
                            onClickFunction = {
                                val primary = if (selectedPrimaryOption == "__NEW__") {
                                    (document.getElementById("cc-primary-input") as? org.w3c.dom.HTMLInputElement)?.value?.trim() ?: ""
                                } else {
                                    selectedPrimaryOption.trim()
                                }
                                val secondary = (document.getElementById("cc-secondary-input") as? org.w3c.dom.HTMLInputElement)?.value?.trim() ?: ""
                                if (primary.isBlank() || secondary.isBlank()) {
                                    createError = "Both fields are required"
                                    updateModalContent()
                                } else if (apiService != null) {
                                    MainScope().launch {
                                        try {
                                            val created = apiService.createCustomCategory(primary, secondary, createIsIncome)
                                            customCategories = customCategories + created
                                            showCreateForm = false
                                            createError = null
                                            // Tell the host so its customCategories cache reloads; otherwise
                                            // DOM patches after applying this sub won't find its icon.
                                            onCategoryColorChanged?.invoke()
                                            updateModalContent()
                                        } catch (e: Exception) {
                                            createError = "Failed to save: ${e.message}"
                                            updateModalContent()
                                        }
                                    }
                                }
                            }
                            +"Save"
                        }
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textSecondary}; font-size: 15px; cursor: pointer;"
                            onClickFunction = { showCreateForm = false; createError = null; updateModalContent() }
                            +"Back"
                        }
                    }
                    return@div
                }

                // ---------- Modify-mode overlay branches (early-exit) ----------

                // Add-subcategory inner overlay: name + icon grid.
                if (addingSubForPrimary != null) {
                    val primaryName = addingSubForPrimary!!
                    h3 {
                        style = "font-size: 18px; font-weight: 600; color: ${t.textPrimary}; margin: 0 0 4px 0; text-align: center;"
                        +StringKey.CATEGORY_ADD_SUB_TITLE.tr()
                    }
                    p {
                        style = "font-size: 13px; color: ${t.textSecondary}; margin: 0 0 16px 0; text-align: center;"
                        +StringKey.CATEGORY_UNDER_PRIMARY.tr().replace("%s", primaryName)
                    }
                    input {
                        id = "new-sub-name"
                        type = InputType.text
                        placeholder = StringKey.CATEGORY_SUB_NAME_PLACEHOLDER.tr()
                        attributes["value"] = newSubName
                        style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 16px; box-sizing: border-box; outline: none;"
                    }
                    p { style = "color: ${t.textSecondary}; font-size: 12px; margin-bottom: 8px;"; +StringKey.CATEGORY_CHOOSE_ICON.tr() }
                    div {
                        id = "new-sub-icon-grid"
                        style = "margin-bottom: 16px;"
                    }
                    if (addSubError != null) {
                        p {
                            style = "color: ${t.expenseRed}; font-size: 13px; margin: 0 0 12px 0;"
                            +addSubError!!
                        }
                    }
                    div {
                        style = "display: flex; gap: 8px;"
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.accent}; border: none; border-radius: 8px; color: ${if (t.isDark) "#000" else "#fff"}; font-size: 15px; font-weight: 700; cursor: pointer;"
                            onClickFunction = {
                                val input = document.getElementById("new-sub-name") as? HTMLInputElement
                                val name = input?.value?.trim().orEmpty()
                                if (name.isNotEmpty() && apiService != null) {
                                    MainScope().launch {
                                        try {
                                            val created = apiService.createCustomCategory(
                                                primaryName = primaryName,
                                                secondaryName = name,
                                                isIncome = !transaction.isDebit,
                                                icon = newSubIcon,
                                            )
                                            customCategories = customCategories + created
                                            addingSubForPrimary = null
                                            newSubName = ""
                                            newSubIcon = utils.CustomCategoryIcons.ids.first()
                                            addSubError = null
                                            // Tell the host so its customCategories cache reloads;
                                            // otherwise DOM patches after applying this sub won't find its icon.
                                            onCategoryColorChanged?.invoke()
                                            // If we came from primary-edit, editingPrimaryName is still
                                            // set, so the re-render lands back on primary-edit with the
                                            // new sub visible in its read-only sub list.
                                            updateModalContent()
                                        } catch (e: Exception) {
                                            addSubError = e.message ?: "Failed to add subcategory"
                                            updateModalContent()
                                        }
                                    }
                                }
                            }
                            +StringKey.ACTION_SAVE.tr()
                        }
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textSecondary}; font-size: 15px; cursor: pointer;"
                            onClickFunction = {
                                addingSubForPrimary = null
                                newSubName = ""
                                newSubIcon = utils.CustomCategoryIcons.ids.first()
                                addSubError = null
                                updateModalContent()
                            }
                            +StringKey.ACTION_BACK.tr()
                        }
                    }
                    return@div
                }

                // ---------- Edit-subcategory overlay (rename + icon) ----------
                if (editingSubId != null) {
                    val editing = customCategories.firstOrNull { it.id == editingSubId }
                    if (editing == null) {
                        editingSubId = null
                        updateModalContent()
                        return@div
                    }
                    h3 {
                        style = "font-size: 18px; font-weight: 600; color: ${t.textPrimary}; margin: 0 0 4px 0; text-align: center;"
                        +StringKey.CATEGORY_EDIT_SUB_TITLE.tr()
                    }
                    p {
                        style = "font-size: 13px; color: ${t.textSecondary}; margin: 0 0 16px 0; text-align: center;"
                        +StringKey.CATEGORY_UNDER_PRIMARY.tr().replace("%s", editing.primaryName)
                    }
                    input {
                        id = "edit-sub-name"
                        type = InputType.text
                        attributes["value"] = editSubName
                        style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 16px; box-sizing: border-box; outline: none;"
                    }
                    p { style = "color: ${t.textSecondary}; font-size: 12px; margin-bottom: 8px;"; +StringKey.CATEGORY_CHOOSE_ICON.tr() }
                    div {
                        id = "edit-sub-icon-grid"
                        style = "margin-bottom: 16px;"
                    }
                    if (editSubError != null) {
                        p {
                            style = "color: ${t.expenseRed}; font-size: 13px; margin: 0 0 12px 0;"
                            +editSubError!!
                        }
                    }
                    div {
                        style = "display: flex; gap: 8px;"
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.accent}; border: none; border-radius: 8px; color: ${if (t.isDark) "#000" else "#fff"}; font-size: 15px; font-weight: 700; cursor: pointer;"
                            onClickFunction = {
                                val input = document.getElementById("edit-sub-name") as? HTMLInputElement
                                val newName = input?.value?.trim().orEmpty()
                                when {
                                    newName.isEmpty() -> {
                                        editSubError = "Subcategory name cannot be empty"
                                        updateModalContent()
                                    }
                                    apiService != null -> {
                                        MainScope().launch {
                                            try {
                                                val updated = apiService.updateCustomCategory(
                                                    id = editing.id,
                                                    secondaryName = newName,
                                                    icon = editSubIcon,
                                                )
                                                customCategories = customCategories.map {
                                                    if (it.id == updated.id) updated else it
                                                }
                                                editingSubId = null
                                                editSubError = null
                                                // Reload host cache so later DOM patches use the new icon/name.
                                                onCategoryColorChanged?.invoke()
                                                updateModalContent()
                                            } catch (e: Exception) {
                                                editSubError = e.message ?: "Failed to save"
                                                updateModalContent()
                                            }
                                        }
                                    }
                                }
                            }
                            +StringKey.ACTION_SAVE.tr()
                        }
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textSecondary}; font-size: 15px; cursor: pointer;"
                            onClickFunction = {
                                editingSubId = null
                                editSubError = null
                                updateModalContent()
                            }
                            +StringKey.ACTION_BACK.tr()
                        }
                    }
                    return@div
                }

                // Primary-edit overlay: editable name (user-made only) + colour picker +
                // "+ Add a subcategory" button. Sub rename/delete now lives entirely in
                // the drill-down Modify view, not here.
                if (editingPrimaryName != null) {
                    val primaryName = editingPrimaryName!!
                    val isUserPrimary = !isPlaidPrimary(primaryName)

                    // Header: back + centered title.
                    div {
                        style = "display: flex; align-items: center; gap: 8px; margin-bottom: 16px;"
                        button {
                            style = "background: none; border: none; color: ${t.accent}; cursor: pointer; font-size: 14px;"
                            onClickFunction = {
                                editingPrimaryName = null
                                editPrimaryError = null
                                updateModalContent()
                            }
                            +("← " + StringKey.ACTION_BACK.tr())
                        }
                        h3 {
                            style = "flex: 1; text-align: center; font-size: 18px; font-weight: 600; color: ${t.textPrimary}; margin: 0;"
                            +primaryName
                        }
                    }

                    // Editable name (user-made primaries only).
                    if (isUserPrimary) {
                        p { style = "color: ${t.textSecondary}; font-size: 12px; margin: 0 0 6px 0;"; +StringKey.CATEGORY_PRIMARY_NAME_LABEL.tr() }
                        input {
                            id = "primary-edit-name"
                            type = InputType.text
                            attributes["value"] = primaryName
                            style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 16px; box-sizing: border-box; outline: none;"
                        }
                    }

                    // Colour picker (available for any primary — user or Plaid override).
                    p { style = "color: ${t.textSecondary}; font-size: 12px; margin: 0 0 8px 0;"; +StringKey.CATEGORY_COLOUR.tr() }
                    div {
                        id = "primary-edit-color"
                        style = "margin-bottom: 16px;"
                    }

                    // "+ Add a subcategory" — if the user typed a new name in the input,
                    // commit the rename first so the sub gets created under the new name
                    // (and so the primary doesn't appear to "snap back" to the old name).
                    button {
                        style = "width: 100%; padding: 10px 14px; background: ${t.border}; border: 1px dashed ${t.accent}; border-radius: 8px; color: ${t.accent}; font-size: 14px; font-weight: 500; cursor: pointer; text-align: left; margin-bottom: 16px;"
                        onClickFunction = {
                            val input = document.getElementById("primary-edit-name") as? HTMLInputElement
                            val entered = input?.value?.trim().orEmpty()
                            val needsRename = isUserPrimary && entered.isNotEmpty() &&
                                !entered.equals(primaryName, ignoreCase = true)
                            when {
                                isUserPrimary && entered.isEmpty() -> {
                                    editPrimaryError = "Primary name cannot be empty"
                                    updateModalContent()
                                }
                                needsRename && apiService != null -> {
                                    MainScope().launch {
                                        try {
                                            val affected = customCategories.filter {
                                                it.primaryName.equals(primaryName, ignoreCase = true)
                                            }
                                            affected.forEach { row ->
                                                apiService.updateCustomCategory(id = row.id, primaryName = entered)
                                            }
                                            customCategories = customCategories.map {
                                                if (it.primaryName.equals(primaryName, ignoreCase = true))
                                                    it.copy(primaryName = entered)
                                                else it
                                            }
                                            val colors = userCategoryColors.toMutableMap()
                                            val oldUpper = primaryName.uppercase().replace(' ', '_')
                                            val oldLower = primaryName.lowercase().replace(' ', '_')
                                            val existing = colors[oldUpper] ?: colors[oldLower]
                                            if (existing != null) {
                                                colors[entered.uppercase().replace(' ', '_')] = existing
                                                colors[entered.lowercase().replace(' ', '_')] = existing
                                                colors.remove(oldUpper)
                                                colors.remove(oldLower)
                                                userCategoryColors = colors
                                                try {
                                                    val colorJson = kotlinx.serialization.json.Json.encodeToString(
                                                        kotlinx.serialization.serializer<Map<String, String>>(),
                                                        userCategoryColors,
                                                    )
                                                    apiService.setSetting("custom_category_colors", colorJson)
                                                } catch (_: Exception) { }
                                            }
                                            editingPrimaryName = entered
                                            addingSubForPrimary = entered
                                            newSubName = ""
                                            newSubIcon = utils.CustomCategoryIcons.ids.first()
                                            editPrimaryError = null
                                            onCategoryColorChanged?.invoke()
                                            updateModalContent()
                                        } catch (e: Exception) {
                                            editPrimaryError = e.message ?: "Failed to rename"
                                            updateModalContent()
                                        }
                                    }
                                }
                                else -> {
                                    addingSubForPrimary = primaryName
                                    newSubName = ""
                                    newSubIcon = utils.CustomCategoryIcons.ids.first()
                                    updateModalContent()
                                }
                            }
                        }
                        +("+ " + StringKey.CATEGORY_ADD_SUBCATEGORY.tr())
                    }

                    // Read-only list of subs for context only. Sub edits happen in the
                    // drill-down Modify view (reachable via "All Categories" → tap primary).
                    run {
                        val plaidCat = filteredCategories.firstOrNull { it.name.equals(primaryName, ignoreCase = true) }
                        val plaidSubs = plaidCat?.subcategories?.map { it.name }.orEmpty()
                        val customSubs = customCategories
                            .filter { it.primaryName.equals(primaryName, ignoreCase = true) }
                            .map { it.secondaryName }
                        val subs = (plaidSubs + customSubs).distinct()
                        if (subs.isNotEmpty()) {
                            p { style = "color: ${t.textSecondary}; font-size: 12px; margin: 0 0 6px 0;"; +StringKey.CATEGORY_SUBCATEGORIES_LABEL.tr() }
                            div {
                                style = "margin-bottom: 16px; padding: 8px 12px; background: ${t.border}; border-radius: 8px;"
                                subs.forEach { sub ->
                                    div {
                                        style = "font-size: 13px; color: ${t.textSecondary}; padding: 3px 0; cursor: default;"
                                        +"• $sub"
                                    }
                                }
                            }
                        }
                    }

                    if (editPrimaryError != null) {
                        p {
                            style = "color: ${t.expenseRed}; font-size: 13px; margin: 0 0 12px 0;"
                            +editPrimaryError!!
                        }
                    }

                    // Save / Back action row. Save commits a rename (if changed) by
                    // iterating PUT per custom row that shares the old primary name,
                    // then mirrors the colour to the new-name key so the UI finds it.
                    if (isUserPrimary) {
                        div {
                            style = "display: flex; gap: 8px;"
                            button {
                                style = "flex: 1; padding: 12px; background: ${t.accent}; border: none; border-radius: 8px; color: ${if (t.isDark) "#000" else "#fff"}; font-size: 15px; font-weight: 700; cursor: pointer;"
                                onClickFunction = {
                                    val input = document.getElementById("primary-edit-name") as? HTMLInputElement
                                    val newName = input?.value?.trim().orEmpty()
                                    when {
                                        newName.isEmpty() -> {
                                            editPrimaryError = "Primary name cannot be empty"
                                            updateModalContent()
                                        }
                                        newName.equals(primaryName, ignoreCase = true) -> {
                                            // No-op rename — just close.
                                            editingPrimaryName = null
                                            editPrimaryError = null
                                            updateModalContent()
                                        }
                                        apiService != null -> {
                                            MainScope().launch {
                                                try {
                                                    val affected = customCategories.filter {
                                                        it.primaryName.equals(primaryName, ignoreCase = true)
                                                    }
                                                    affected.forEach { row ->
                                                        apiService.updateCustomCategory(
                                                            id = row.id,
                                                            primaryName = newName,
                                                        )
                                                    }
                                                    customCategories = customCategories.map {
                                                        if (it.primaryName.equals(primaryName, ignoreCase = true))
                                                            it.copy(primaryName = newName)
                                                        else it
                                                    }
                                                    // Carry the colour override to the new name keys.
                                                    val colors = userCategoryColors.toMutableMap()
                                                    val oldUpper = primaryName.uppercase().replace(' ', '_')
                                                    val oldLower = primaryName.lowercase().replace(' ', '_')
                                                    val existing = colors[oldUpper] ?: colors[oldLower]
                                                    if (existing != null) {
                                                        colors[newName.uppercase().replace(' ', '_')] = existing
                                                        colors[newName.lowercase().replace(' ', '_')] = existing
                                                        colors.remove(oldUpper)
                                                        colors.remove(oldLower)
                                                        userCategoryColors = colors
                                                        try {
                                                            val colorJson = kotlinx.serialization.json.Json.encodeToString(
                                                                kotlinx.serialization.serializer<Map<String, String>>(),
                                                                userCategoryColors,
                                                            )
                                                            apiService.setSetting("custom_category_colors", colorJson)
                                                        } catch (_: Exception) { /* best-effort */ }
                                                    }
                                                    editingPrimaryName = null
                                                    editPrimaryError = null
                                                    onCategoryColorChanged?.invoke()
                                                    updateModalContent()
                                                } catch (e: Exception) {
                                                    editPrimaryError = e.message ?: "Failed to rename"
                                                    updateModalContent()
                                                }
                                            }
                                        }
                                    }
                                }
                                +StringKey.ACTION_SAVE.tr()
                            }
                        }
                    }
                    return@div
                }

                // Add-primary overlay: name + HSV colour + first sub + icon.
                if (showAddPrimaryOverlay) {
                    h3 {
                        style = "font-size: 18px; font-weight: 600; color: ${t.textPrimary}; margin: 0 0 16px 0; text-align: center;"
                        +StringKey.CATEGORY_ADD_PRIMARY.tr()
                    }
                    input {
                        id = "add-primary-name"
                        type = InputType.text
                        placeholder = StringKey.CATEGORY_PRIMARY_NAME_PLACEHOLDER.tr()
                        style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 12px; box-sizing: border-box; outline: none;"
                        attributes["value"] = addPrimaryName
                    }
                    input {
                        id = "add-primary-first-sub"
                        type = InputType.text
                        placeholder = StringKey.CATEGORY_FIRST_SUB_PLACEHOLDER.tr()
                        style = "width: 100%; padding: 10px 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textPrimary}; font-size: 15px; margin-bottom: 16px; box-sizing: border-box; outline: none;"
                        attributes["value"] = addPrimaryFirstSub
                    }
                    p { style = "color: ${t.textSecondary}; font-size: 12px; margin: 0 0 8px 0;"; +StringKey.CATEGORY_COLOUR.tr() }
                    div {
                        id = "add-primary-color"
                        style = "margin-bottom: 16px;"
                    }
                    p { style = "color: ${t.textSecondary}; font-size: 12px; margin-bottom: 8px;"; +StringKey.CATEGORY_FIRST_SUB_ICON_LABEL.tr() }
                    div { id = "add-primary-icon-grid"; style = "margin-bottom: 16px;" }
                    div {
                        style = "display: flex; gap: 8px;"
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.accent}; border: none; border-radius: 8px; color: ${if (t.isDark) "#000" else "#fff"}; font-size: 15px; font-weight: 700; cursor: pointer;"
                            onClickFunction = {
                                val pName = (document.getElementById("add-primary-name") as? HTMLInputElement)?.value?.trim().orEmpty()
                                val sName = (document.getElementById("add-primary-first-sub") as? HTMLInputElement)?.value?.trim().orEmpty()
                                if (pName.isNotEmpty() && sName.isNotEmpty() && apiService != null) {
                                    MainScope().launch {
                                        try {
                                            val created = apiService.createCustomCategory(pName, sName, !transaction.isDebit, addPrimaryIcon)
                                            customCategories = customCategories + created
                                            // Persist colour override
                                            val colors = userCategoryColors.toMutableMap()
                                            colors[pName.uppercase().replace(' ', '_')] = addPrimaryColor
                                            colors[pName.lowercase().replace(' ', '_')] = addPrimaryColor
                                            userCategoryColors = colors
                                            try {
                                                val colorJson = kotlinx.serialization.json.Json.encodeToString(
                                                    kotlinx.serialization.serializer<Map<String, String>>(),
                                                    userCategoryColors,
                                                )
                                                apiService.setSetting("custom_category_colors", colorJson)
                                            } catch (_: Exception) { /* best-effort */ }
                                            showAddPrimaryOverlay = false
                                            addPrimaryName = ""
                                            addPrimaryFirstSub = ""
                                            addPrimaryIcon = utils.CustomCategoryIcons.ids.first()
                                            addPrimaryColor = "#00D9FF"
                                            updateModalContent()
                                            onCategoryColorChanged?.invoke()
                                        } catch (e: Exception) {
                                            console.error("Add primary failed: ${e.message}")
                                        }
                                    }
                                }
                            }
                            +StringKey.ACTION_SAVE.tr()
                        }
                        button {
                            style = "flex: 1; padding: 12px; background: ${t.border}; border: 1px solid ${t.chipUnselectedBorder}; border-radius: 8px; color: ${t.textSecondary}; font-size: 15px; cursor: pointer;"
                            onClickFunction = {
                                showAddPrimaryOverlay = false
                                updateModalContent()
                            }
                            +StringKey.ACTION_BACK.tr()
                        }
                    }
                    return@div
                }

                // Header: "Choose a category (Modify / Done)"
                div {
                    style = "display: flex; align-items: center; justify-content: center; gap: 8px; margin: 0 0 16px 0;"
                    h3 {
                        style = "font-size: 20px; font-weight: 600; color: ${t.textPrimary}; margin: 0;"
                        +i18n.StringKey.CATEGORY_CHOOSE.tr()
                    }
                    span {
                        style = "color: ${t.accent}; font-size: 14px; cursor: pointer; font-weight: 500; margin-left: 12px;"
                        onClickFunction = {
                            modifyMode = !modifyMode
                            // Reset transient modify sub-states when leaving the mode
                            if (!modifyMode) {
                                editingPrimaryName = null
                                addingSubForPrimary = null
                                editingSubId = null
                                showAddPrimaryOverlay = false
                            }
                            updateModalContent()
                        }
                        +(if (modifyMode) StringKey.ACTION_DONE.tr() else StringKey.CATEGORY_MODIFY.tr())
                    }
                }

                // Breadcrumb navigation
                if (selectedPrimary != null) {
                    div {
                        style = """
                            font-size: 14px;
                            color: ${t.textSecondary};
                            margin-bottom: 16px;
                            display: flex;
                            align-items: center;
                            gap: 8px;
                        """

                        button {
                            style = """
                                background: none;
                                border: none;
                                color: ${t.accent};
                                cursor: pointer;
                                text-decoration: underline;
                                font-size: 14px;
                            """
                            onClickFunction = {
                                // Leaving the sub-list view cancels any sub-level Modify
                                // session so the two levels stay independent.
                                selectedPrimary = null
                                modifyMode = false
                                addingSubForPrimary = null
                                editingSubId = null
                                updateModalContent()
                            }
                            +StringKey.CATEGORY_ALL_CATEGORIES.tr()
                        }

                        +" > ${selectedPrimary!!.name}"
                    }
                }

                // Category selection using SelectableCategory (filtered by transaction type)
                if (selectedPrimary != null) {
                    // Show subcategories for selected primary
                    h4 {
                        style = "font-size: 16px; color: ${t.textSecondary}; margin: 0 0 12px 0;"
                        +StringKey.CATEGORY_CHOOSE_SUBCATEGORY.tr()
                    }
                    // Modify mode: "+ Add a subcategory" at top of the list.
                    if (modifyMode) {
                        button {
                            style = "width: 100%; padding: 10px 14px; background: ${t.border}; border: 1px dashed ${t.accent}; border-radius: 8px; color: ${t.accent}; font-size: 14px; font-weight: 500; cursor: pointer; text-align: left; margin-bottom: 6px;"
                            onClickFunction = {
                                addingSubForPrimary = selectedPrimary!!.name
                                newSubName = ""
                                newSubIcon = utils.CustomCategoryIcons.ids.first()
                                updateModalContent()
                            }
                            +("+ " + StringKey.CATEGORY_ADD_SUBCATEGORY.tr())
                        }
                    }
                    // For user-made primaries, selectedPrimary.subcategories holds the same
                    // custom rows the filter-path below renders. Iterating here would duplicate
                    // them (with the wrong icons, since plaidCode is just the display name).
                    // Skip this loop for user-made primaries — the filter path handles them.
                    val selectedIsPlaidPrimary = isPlaidPrimary(selectedPrimary!!.name)
                    if (selectedIsPlaidPrimary) selectedPrimary!!.subcategories.forEach { subcategory ->
                        val isSelected = subcategory.name == transaction.category
                        val subColor = getCategoryColor(subcategory.name)
                        // Plaid built-in subs are read-only in Modify mode — dim them and
                        // swallow clicks so the mental model ("Modify = edit user-made things")
                        // holds.
                        button {
                            style = """
                                width: 100%;
                                padding: 10px 16px;
                                margin-bottom: 6px;
                                background: ${if (isSelected) t.accent else t.border};
                                border: 1px solid ${if (isSelected) t.accent else t.border};
                                border-radius: 8px;
                                color: ${if (isSelected) (if (t.isDark) "#000" else "#fff") else t.textPrimary};
                                font-size: 15px;
                                cursor: ${if (modifyMode) "default" else "pointer"};
                                text-align: left;
                                transition: all 0.2s ease;
                                display: flex;
                                align-items: center;
                                gap: 10px;
                                opacity: ${if (modifyMode) "0.55" else "1"};
                            """
                            if (!modifyMode) {
                                onMouseOver = "if (!$isSelected) { this.style.backgroundColor='${t.chipUnselectedBorder}'; this.style.borderColor='${t.accent}'; }"
                                onMouseOut = "if (!$isSelected) { this.style.backgroundColor='${t.border}'; this.style.borderColor='${t.border}'; }"
                            }
                            onClickFunction = {
                                if (!modifyMode) {
                                    // Only clear a goal assignment if there actually is one —
                                    // skips the noisy 404 network log for the common case.
                                    if (apiService != null && transaction.assignedGoalId != null) {
                                        MainScope().launch {
                                            try {
                                                apiService.removeGoalAssignment(transaction.id)
                                            } catch (e: Exception) {
                                                // Ignore — best-effort cleanup.
                                            }
                                        }
                                    }
                                    // Send "PRIMARY/DETAILED" Plaid codes format
                                    val categoryCode = "${selectedPrimary!!.plaidCode}/${subcategory.plaidCode}"
                                    pendingCategoryCode = categoryCode
                                    updateModalContent()
                                }
                            }
                            // Category SVG icon (bare, no circle)
                            span {
                                style = "flex-shrink: 0; display: flex; align-items: center;"
                                unsafe { +CategoryIconSvg.renderForCategory(subcategory.plaidCode, size = 16, color = if (isSelected) (if (t.isDark) "#000" else "#fff") else t.textSecondary) }
                            }
                            // Subcategory name
                            span { style = "flex: 1;"; +subcategory.name }
                            // Checkmark for selected
                            if (isSelected) {
                                span { style = "color: ${if (t.isDark) "#000" else "#fff"}; font-size: 14px; font-weight: bold;"; +"✓" }
                            }
                        }
                    }
                    // Show matching custom subcategories within this primary category
                    customCategories.filter {
                        it.primaryName.equals(selectedPrimary!!.name, ignoreCase = true)
                    }.forEach { custom ->
                        val isSelected = custom.secondaryName == transaction.category
                        val customColor = getCategoryColor(custom.secondaryName)
                        div {
                            style = "display: flex; align-items: center; gap: 8px; margin-bottom: 6px;"
                            button {
                                style = """
                                    flex: 1;
                                    padding: 10px 16px;
                                    background: ${if (isSelected) t.accent else t.border};
                                    border: 1px solid ${if (isSelected) t.accent else t.border};
                                    border-radius: 8px;
                                    color: ${if (isSelected) (if (t.isDark) "#000" else "#fff") else t.textPrimary};
                                    font-size: 15px;
                                    cursor: pointer;
                                    text-align: left;
                                    transition: all 0.2s ease;
                                    display: flex;
                                    align-items: center;
                                    gap: 10px;
                                """
                                onMouseOver = "if (!$isSelected) { this.style.backgroundColor='${t.chipUnselectedBorder}'; this.style.borderColor='${t.accent}'; }"
                                onMouseOut = "if (!$isSelected) { this.style.backgroundColor='${t.border}'; this.style.borderColor='${t.border}'; }"
                                onClickFunction = {
                                    if (modifyMode) {
                                        // Tapping a user sub in Modify mode opens the rename/icon editor;
                                        // the '-' button (rendered as a separate sibling) handles delete.
                                        editingSubId = custom.id
                                        editSubName = custom.secondaryName
                                        editSubIcon = custom.icon?.takeIf { it.isNotBlank() }
                                            ?: utils.CustomCategoryIcons.ids.first()
                                        editSubError = null
                                        updateModalContent()
                                    } else {
                                        val categoryCode = "${custom.primaryName}/${custom.secondaryName}"
                                        pendingCategoryCode = categoryCode
                                        updateModalContent()
                                    }
                                }
                                // Category SVG icon — prefer the user-picked icon id; fall back
                                // to the name-based guess for legacy subs that have no icon set.
                                val iconColor = if (isSelected) (if (t.isDark) "#000" else "#fff") else t.textSecondary
                                val customSvg = utils.CustomCategoryIcons.renderSvg(custom.icon, size = 16)
                                span {
                                    style = "flex-shrink: 0; display: flex; align-items: center; color: $iconColor;"
                                    unsafe {
                                        +(customSvg ?: CategoryIconSvg.renderForCategory(custom.secondaryName, size = 16, color = iconColor))
                                    }
                                }
                                // Custom subcategory name
                                span { style = "flex: 1;"; +custom.secondaryName }
                                if (isSelected) {
                                    span { style = "color: ${if (t.isDark) "#000" else "#fff"}; font-size: 14px; font-weight: bold;"; +"✓" }
                                }
                            }
                            // '-' delete button, visible only in Modify mode (custom subs are always user-made)
                            if (modifyMode) {
                                span {
                                    style = """
                                        width: 24px; height: 24px;
                                        border-radius: 50%;
                                        background: rgba(255, 107, 107, 0.15);
                                        border: 1px solid rgba(255, 107, 107, 0.45);
                                        color: ${t.expenseRed};
                                        display: inline-flex; align-items: center; justify-content: center;
                                        cursor: pointer; flex-shrink: 0;
                                        transition: background 0.15s ease;
                                    """
                                    onClickFunction = {
                                        val isUserPrimary = !isPlaidPrimary(custom.primaryName)
                                        handleDeleteCustomCategory(
                                            custom = custom,
                                            allCustom = customCategories,
                                            isUserCreatedPrimary = isUserPrimary,
                                        ) {
                                            customCategories = customCategories.filterNot { it.id == custom.id }
                                            val primaryName = custom.primaryName
                                            val stillHasSubs = customCategories.any {
                                                it.primaryName.equals(primaryName, ignoreCase = true)
                                            }
                                            if (isUserPrimary && !stillHasSubs) {
                                                // Primary disappears — go back to the primary list.
                                                selectedPrimary = null
                                            }
                                            // Server may have rewritten PFC on affected transactions
                                            // (reassigned or AI-recategorized). Ask the host to
                                            // refresh the transaction list so stale labels clear.
                                            onCategoryColorChanged?.invoke()
                                            updateModalContent()
                                        }
                                    }
                                    unsafe { +"""<svg width="12" height="3" viewBox="0 0 12 3" fill="currentColor" style="display:block;"><rect width="12" height="3" rx="1.5"/></svg>""" }
                                }
                            }
                        }
                    }
                } else {
                    // Show primary categories (filtered by transaction type)
                    // Merge: add custom primaries that don't match any existing Plaid primary
                    val existingPrimaryNames = filteredCategories
                        .filter { it.plaidCode != "__DIVIDER__" }
                        .map { it.name.lowercase() }
                        .toSet()
                    val newCustomPrimaries = customCategories
                        .filter { it.primaryName.lowercase() !in existingPrimaryNames }
                        .groupBy { it.primaryName }
                        .map { (primaryName, customs) ->
                            val subs = customs.map { custom ->
                                SelectableCategory(
                                    name = custom.secondaryName,
                                    plaidCode = custom.secondaryName
                                )
                            }
                            SelectableCategory(
                                name = primaryName,
                                plaidCode = primaryName,
                                subcategories = subs
                            )
                        }
                    val allCategories = filteredCategories + newCustomPrimaries

                    // Modify mode: "+ Add a primary category" cell at top.
                    if (modifyMode) {
                        button {
                            style = "width: 100%; padding: 10px 16px; background: ${t.border}; border: 1px dashed ${t.accent}; border-radius: 8px; color: ${t.accent}; font-size: 15px; font-weight: 500; cursor: pointer; text-align: left; margin-bottom: 6px;"
                            onClickFunction = {
                                showAddPrimaryOverlay = true
                                addPrimaryName = ""
                                addPrimaryFirstSub = ""
                                addPrimaryColor = "#00D9FF"
                                addPrimaryIcon = utils.CustomCategoryIcons.ids.first()
                                updateModalContent()
                            }
                            +("+ " + StringKey.CATEGORY_ADD_PRIMARY.tr())
                        }
                    }

                    allCategories.forEach { category ->
                        if (category.plaidCode == "__DIVIDER__") {
                            div {
                                style = "display: flex; align-items: center; gap: 8px; margin: 12px 0;"
                                hr { style = "flex: 1; border: none; border-top: 1px solid ${t.chipUnselectedBorder}; margin: 0;" }
                                span {
                                    style = "font-size: 11px; color: ${t.textSecondary}; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.08em; padding: 0 6px;"
                                    +category.name
                                }
                                hr { style = "flex: 1; border: none; border-top: 1px solid ${t.chipUnselectedBorder}; margin: 0;" }
                            }
                            return@forEach
                        }
                        val catColor = getCategoryColor(category.plaidCode)
                        // Single button per primary (no more separate colour dot)
                        button {
                            style = """
                                width: 100%;
                                padding: 10px 16px;
                                margin-bottom: 6px;
                                background: ${t.border};
                                border: 1px solid ${t.border};
                                border-radius: 8px;
                                color: ${t.textPrimary};
                                font-size: 15px;
                                cursor: pointer;
                                text-align: left;
                                transition: all 0.2s ease;
                                display: flex;
                                align-items: center;
                                gap: 10px;
                            """
                            onMouseOver = "this.style.backgroundColor='${t.chipUnselectedBorder}';"
                            onMouseOut = "this.style.backgroundColor='${t.border}';"
                            onClickFunction = {
                                if (modifyMode) {
                                    editingPrimaryName = category.name
                                    updateModalContent()
                                } else if (category.subcategories.isNotEmpty()) {
                                    selectedPrimary = category
                                    updateModalContent()
                                } else {
                                    if (apiService != null && transaction.assignedGoalId != null) {
                                        MainScope().launch {
                                            try {
                                                apiService.removeGoalAssignment(transaction.id)
                                            } catch (e: Exception) {}
                                        }
                                    }
                                    pendingCategoryCode = category.plaidCode
                                    updateModalContent()
                                }
                            }
                            // Circled icon — for user-made primaries, reuse the first sub's
                            // icon as the primary's visual identity; for Plaid primaries,
                            // fall back to the built-in code-based lookup.
                            span {
                                style = """
                                    width: 28px; height: 28px; border-radius: 50%;
                                    background: ${getIconCircleFill(catColor, t.isDark)}; ${if (t.isDark) "border: 2px solid $catColor;" else ""}
                                    display: inline-flex; align-items: center; justify-content: center;
                                    flex-shrink: 0;
                                    color: ${t.iconStroke};
                                """
                                val userIconSvg = findUserIconId(category.name)
                                    ?.let { utils.CustomCategoryIcons.renderSvg(it, size = 14, strokeWidth = 2) }
                                if (userIconSvg != null) {
                                    unsafe { +userIconSvg }
                                } else {
                                    val iconId = utils.CategoryUtils.getCategoryIconId(category.plaidCode)
                                    val svgContent = utils.CategoryIconPaths.getSvgContent(iconId)
                                    unsafe { +"""<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${t.iconStroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">$svgContent</svg>""" }
                                }
                            }
                            // Category name
                            span { style = "flex: 1;"; +category.name }
                            // Right-aligned arrow — hidden in Modify mode
                            if (!modifyMode && category.subcategories.isNotEmpty()) {
                                span { style = "color: ${t.textSecondary}; font-size: 14px;"; +">" }
                            }
                        }
                    }
                }

                // Cancel button
                button {
                    style = """
                        width: 100%;
                        padding: 12px 16px;
                        margin-top: 16px;
                        background: ${t.border};
                        border: 1px solid ${t.chipUnselectedBorder};
                        border-radius: 8px;
                        color: ${t.textSecondary};
                        font-size: 16px;
                        cursor: pointer;
                        text-align: center;
                    """
                    onMouseOver = "this.style.backgroundColor='${t.chipUnselectedBorder}'; this.style.color='${t.textPrimary}';"
                    onMouseOut = "this.style.backgroundColor='${t.border}'; this.style.color='${t.textSecondary}';"
                    onClickFunction = {
                        document.body?.removeChild(modalElement)
                    }
                    +StringKey.ACTION_CANCEL.tr()
                }
            }

            modalElement.appendChild(modalContent)

            // Mount HSV + icon pickers for the Modify overlays (post-DOM-build).
            if (editingPrimaryName != null) {
                val pName = editingPrimaryName!!
                val currentColor = utils.CategoryUtils.getCategoryColor(pName, userCategoryColors)
                (modalElement.querySelector("#primary-edit-color") as? HTMLElement)?.let { slot ->
                    mountHsvColorPicker(slot, currentColor) { newColor ->
                        val colors = userCategoryColors.toMutableMap()
                        colors[pName.uppercase().replace(' ', '_')] = newColor
                        colors[pName.lowercase().replace(' ', '_')] = newColor
                        userCategoryColors = colors
                        MainScope().launch {
                            try {
                                val json = kotlinx.serialization.json.Json.encodeToString(
                                    kotlinx.serialization.serializer<Map<String, String>>(),
                                    userCategoryColors,
                                )
                                apiService?.setSetting("custom_category_colors", json)
                            } catch (_: Exception) { /* best-effort */ }
                        }
                        onCategoryColorChanged?.invoke()
                    }
                }
            }
            if (addingSubForPrimary != null) {
                (modalElement.querySelector("#new-sub-icon-grid") as? HTMLElement)?.let { slot ->
                    fun remount() {
                        mountIconGrid(slot, newSubIcon, t.textPrimary) { picked ->
                            newSubIcon = picked
                            remount()
                        }
                    }
                    remount()
                }
            }
            if (editingSubId != null) {
                (modalElement.querySelector("#edit-sub-icon-grid") as? HTMLElement)?.let { slot ->
                    fun remount() {
                        mountIconGrid(slot, editSubIcon, t.textPrimary) { picked ->
                            editSubIcon = picked
                            remount()
                        }
                    }
                    remount()
                }
            }
            if (showAddPrimaryOverlay) {
                (modalElement.querySelector("#add-primary-color") as? HTMLElement)?.let { slot ->
                    mountHsvColorPicker(slot, addPrimaryColor) { newColor ->
                        addPrimaryColor = newColor
                    }
                }
                (modalElement.querySelector("#add-primary-icon-grid") as? HTMLElement)?.let { slot ->
                    fun remount() {
                        mountIconGrid(slot, addPrimaryIcon, t.textPrimary) { picked ->
                            addPrimaryIcon = picked
                            remount()
                        }
                    }
                    remount()
                }
            }

            modalElement.focus()
        }

        // Fetch user's custom categories
        if (apiService != null) {
            MainScope().launch {
                customCategories = try { apiService.getCustomCategories() } catch (e: Exception) { emptyList() }
                updateModalContent()
            }
        }

        // Initial render
        updateModalContent()

        // Add modal to body
        document.body?.appendChild(modalElement)
        modalElement.focus()
    }

    fun attachToDOM(elementId: String) {
        val rootElement = document.getElementById(elementId)
        if (rootElement != null) {
            rootElement.appendChild(render())
        }
    }

    private fun DIV.createSplitButton(title: String, onClick: () -> Unit) {
        val t = WebTheme.current
        button {
            style = """
                width: 100%;
                padding: 12px 16px;
                background: ${t.iconCircleDefault};
                border: 1px solid #4CAF50;
                border-radius: 8px;
                color: #4CAF50;
                font-size: 16px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
            """
            onMouseOver = "this.style.backgroundColor='#4CAF50'; this.style.color='${t.textPrimary}'"
            onMouseOut = "this.style.backgroundColor='${t.iconCircleDefault}'; this.style.color='#4CAF50'"
            onClickFunction = { onClick() }

            +title
        }
    }

    private fun DIV.createDistributeButton(title: String, onClick: () -> Unit) {
        val t = WebTheme.current
        button {
            style = """
                width: 100%;
                padding: 12px 16px;
                background: ${t.iconCircleDefault};
                border: 1px solid #2196F3;
                border-radius: 8px;
                color: #2196F3;
                font-size: 16px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
            """
            onMouseOver = "this.style.backgroundColor='#2196F3'; this.style.color='${t.textPrimary}'"
            onMouseOut = "this.style.backgroundColor='${t.iconCircleDefault}'; this.style.color='#2196F3'"
            onClickFunction = { onClick() }

            +title
        }
    }

    // Use shared category hierarchy from ml.getCategoryHierarchy()
    private val sharedHierarchy by lazy { getCategoryHierarchy() }

    private fun getSharedPrimaryCategories(): List<String> =
        sharedHierarchy.primaryCategories.keys.toList().sorted()

    // Plaid uses a 2-level hierarchy: primary -> detailed categories directly
    private fun getSharedSubcategories(primary: String): List<String> {
        return sharedHierarchy.primaryCategories[primary]?.sorted() ?: listOf("Miscellaneous")
    }

    /**
     * Maps AI-predicted category codes (which may reference invalid codes like
     * GENERAL_SERVICES_SOFTWARE) to valid dropdown options from our PlaidCategories list.
     * Falls back gracefully: primary defaults to GENERAL_MERCHANDISE, sub to the "OTHER_" variant.
     */
    private fun resolveValidCategories(primaryCode: String, detailedCode: String): Pair<String, String> {
        val validPrimaries = getSharedPrimaryCategories()
        val primary = if (primaryCode in validPrimaries) primaryCode else "GENERAL_MERCHANDISE"
        val validSubs = getSharedSubcategories(primary)
        val sub = if (detailedCode in validSubs) detailedCode else {
            validSubs.find { it.contains("_OTHER_") } ?: validSubs.firstOrNull() ?: "GENERAL_MERCHANDISE_OTHER_GENERAL_MERCHANDISE"
        }
        return Pair(primary, sub)
    }

    /**
     * Parses CSV/text content and AI-categorizes each row into SplitItemData.
     * Returns null if parsing fails or yields no rows.
     * Both the file import handler and the paste handler call this single function.
     */
    private suspend fun parseCsvAndCategorize(content: String): List<SplitItemData>? {
        val rows = parseCsvToRows(content)
        if (rows == null || rows.isEmpty()) return null
        return rows.map { (amount, description) ->
            val prediction = ml.categorizeTransactionWithAI(
                merchantName = description,
                amount = amount,
                description = description
            )
            val rawPrimary = prediction.categoryLevel1.ifEmpty { prediction.category.ifEmpty { "GENERAL_MERCHANDISE" } }
            val rawSub = prediction.categoryLevel2.ifEmpty { "GENERAL_MERCHANDISE_OTHER_GENERAL_MERCHANDISE" }
            val (primary, sub) = resolveValidCategories(rawPrimary, rawSub)
            console.log("[CSV] '$description' → rawPrimary='$rawPrimary' rawSub='$rawSub' source='${prediction.source}' → resolved: primary='$primary' sub='$sub'")
            SplitItemData(amount = amount, primaryCategory = primary, subCategory = sub, note = description)
        }
    }

    private fun matchCategoryFromInput(input: String): Pair<String, String> {
        val lowercased = input.lowercase()
        val primaries = getSharedPrimaryCategories()
        // First try to match primary category
        val matchedPrimary = primaries.find { it.lowercase().contains(lowercased) }
        if (matchedPrimary != null) {
            val subs = getSharedSubcategories(matchedPrimary)
            return Pair(matchedPrimary, subs.firstOrNull() ?: "Miscellaneous")
        }
        // Then try to match subcategory
        for (primary in primaries) {
            val subs = getSharedSubcategories(primary)
            val matchedSub = subs.find { it.lowercase().contains(lowercased) }
            if (matchedSub != null) {
                return Pair(primary, matchedSub)
            }
        }
        return Pair("Other", "Miscellaneous")
    }

    private fun showSplitModal() {
        val t = WebTheme.current
        val parentAmount = kotlin.math.abs(transaction.amount)

        // Get parent's category for default split items
        // Format raw Plaid categories (UPPER_SNAKE_CASE) to match dropdown options (Title Case)
        val rawPrimary = transaction.categoryLevel1.ifEmpty { "Other" }
        val rawDetailed = transaction.categoryLevel2.ifEmpty { "Miscellaneous" }
        val formattedPrimary = utils.CategoryUtils.formatCategoryName(rawPrimary)

        // Extract subcategory name by removing primary prefix and formatting
        val subcatSuffix = rawDetailed.removePrefix(rawPrimary).removePrefix("_")
        val formattedSubcat = if (subcatSuffix.isEmpty()) {
            utils.CategoryUtils.formatCategoryName(rawDetailed)
        } else {
            utils.CategoryUtils.formatCategoryName(subcatSuffix)
        }

        // Find matching categories from hierarchy, fallback to formatted values
        val availablePrimaries = getSharedPrimaryCategories()
        val parentPrimaryCategory = availablePrimaries.find { it.equals(formattedPrimary, ignoreCase = true) } ?: formattedPrimary
        val availableSubs = getSharedSubcategories(parentPrimaryCategory)
        val parentSubCategory = availableSubs.find { it.equals(formattedSubcat, ignoreCase = true) } ?: availableSubs.firstOrNull() ?: "Miscellaneous"

        // Create modal element
        val modalElement = document.createElement("div") as HTMLElement
        modalElement.style.cssText = """
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        """
        modalElement.addEventListener("click", { event ->
            if (event.target == modalElement) document.body?.removeChild(modalElement)
        })

        // State for split items - initialize with parent's category
        var splitItems = mutableListOf(SplitItemData(primaryCategory = parentPrimaryCategory, subCategory = parentSubCategory))
        var inputMode = "manual"
        var isFromCsv = false   // compact review mode after CSV parse
        var csvText = ""
        var csvParseError: String? = null
        var customCategories: List<CustomCategoryData> = emptyList()

        fun roundTwo(v: Double): Double = kotlin.math.round(v * 100.0) / 100.0
        fun getAllocatedAmount(): Double = roundTwo(splitItems.sumOf { it.amount })
        fun getRemainingAmount(): Double = roundTwo(parentAmount - getAllocatedAmount())

        // Handle amount update - preserve credit sign (negative = credit/refund)
        fun handleAmountUpdate(index: Int, newAmount: Double) {
            val isCredit = splitItems[index].amount < 0
            val rounded = roundTwo(if (isCredit) -kotlin.math.abs(newAmount) else newAmount)
            splitItems[index] = splitItems[index].copy(amount = rounded)
        }

        // Add a new split item with the remaining amount (returns true if item was added)
        fun addNextSplitItem(): Boolean {
            val remaining = getRemainingAmount()
            if (remaining > 0.01) {
                splitItems.add(SplitItemData(
                    amount = roundTwo(remaining),
                    primaryCategory = parentPrimaryCategory,
                    subCategory = parentSubCategory
                ))
                return true
            }
            return false
        }

        // Update just the remaining amount display without rebuilding the entire modal
        // This prevents the "click eaten by DOM rebuild" issue
        fun updateRemainingDisplay() {
            val remaining = getRemainingAmount()
            // Update the add button text if it exists
            document.getElementById("add-split-btn")?.let { btn ->
                btn.textContent = "${StringKey.TRANSACTION_SPLIT_ADD_ITEM.tr()} (${formatCurrency(remaining)} ${StringKey.TRANSACTION_GOAL_REMAINING.tr()})"
                // Show/hide based on remaining amount
                (btn as? org.w3c.dom.HTMLElement)?.style?.display = if (remaining > 0.01) "block" else "none"
            }
            // Update any other remaining amount displays
            document.getElementById("remaining-display")?.let { el ->
                el.textContent = "${formatCurrency(remaining)} ${StringKey.TRANSACTION_GOAL_REMAINING.tr()}"
            }
        }

        fun updateModalContent() {
            modalElement.innerHTML = ""

            val modalContent = document.create.div {
                style = """
                    background: ${t.card};
                    border-radius: 16px;
                    padding: 24px;
                    max-width: 600px;
                    width: 90%;
                    max-height: 80vh;
                    overflow-y: auto;
                    color: ${t.textPrimary};
                """

                // Header
                div {
                    style = """
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 16px;
                    """
                    h3 {
                        style = "font-size: 24px; font-weight: 600; margin: 0;"
                        +StringKey.TRANSACTION_SPLIT_TITLE.tr()
                    }
                    button {
                        style = """
                            background: none;
                            border: none;
                            color: ${t.textPrimary};
                            font-size: 24px;
                            cursor: pointer;
                            padding: 4px;
                        """
                        onClickFunction = { document.body?.removeChild(modalElement) }
                        +"×"
                    }
                }

                p {
                    style = "color: ${t.textSecondary}; margin: 0 0 16px 0;"
                    +"${StringKey.TRANSACTION_DISTRIBUTE_TOTAL.tr()} ${formatCurrency(parentAmount)}"
                }

                // Mode toggle
                div {
                    style = """
                        display: flex;
                        gap: 12px;
                        margin-bottom: 20px;
                    """
                    button {
                        style = """
                            padding: 8px 16px;
                            border-radius: 20px;
                            border: 1px solid ${if (inputMode == "manual") t.accent else t.border};
                            background: ${if (inputMode == "manual") "${t.accent}20" else "transparent"};
                            color: ${if (inputMode == "manual") t.accent else t.textSecondary};
                            cursor: pointer;
                        """
                        onClickFunction = {
                            inputMode = "manual"
                            csvParseError = null
                            updateModalContent()
                        }
                        +StringKey.TRANSACTION_SPLIT_MANUAL_ENTRY.tr()
                    }
                    button {
                        style = """
                            padding: 8px 16px;
                            border-radius: 20px;
                            border: 1px solid ${if (inputMode == "csv") t.accent else t.border};
                            background: ${if (inputMode == "csv") "${t.accent}20" else "transparent"};
                            color: ${if (inputMode == "csv") t.accent else t.textSecondary};
                            cursor: pointer;
                        """
                        onClickFunction = {
                            inputMode = "csv"
                            csvParseError = null
                            updateModalContent()
                        }
                        +StringKey.TRANSACTION_SPLIT_CSV_IMPORT.tr()
                    }
                }

                if (inputMode == "manual") {
                    // Split items
                    splitItems.forEachIndexed { index, item ->
                        val isCredit = item.amount < 0

                        if (isFromCsv) {
                            // Compact CSV review row: amount | name + category | delete
                            div {
                                style = """
                                    background: ${t.iconCircleDefault};
                                    border-radius: 8px;
                                    padding: 8px 12px;
                                    margin-bottom: 6px;
                                    display: flex;
                                    align-items: center;
                                    gap: 10px;
                                """
                                // Delete button
                                if (splitItems.size > 1) {
                                    button {
                                        style = "background: none; border: none; color: #ff6b6b; cursor: pointer; font-size: 16px; flex-shrink: 0; line-height: 1; padding: 0 2px;"
                                        onClickFunction = {
                                            splitItems.removeAt(index)
                                            updateModalContent()
                                        }
                                        +"×"
                                    }
                                }
                                // Amount input (narrow)
                                input {
                                    type = InputType.number
                                    placeholder = "0.00"
                                    value = kotlin.math.abs(item.amount).let { if (it > 0) it.toString() else "" }
                                    style = """
                                        width: 80px;
                                        padding: 4px 8px;
                                        border: 1px solid ${if (isCredit) "#4CAF5066" else t.border};
                                        border-radius: 6px;
                                        background: ${t.card};
                                        color: ${if (isCredit) "#4CAF50" else "white"};
                                        font-size: 14px;
                                        flex-shrink: 0;
                                    """
                                    onInputFunction = { e ->
                                        val value = (e.target as? org.w3c.dom.HTMLInputElement)?.value?.toDoubleOrNull() ?: 0.0
                                        handleAmountUpdate(index, value)
                                        updateRemainingDisplay()
                                    }
                                }
                                // Description + category (small, read-only)
                                div {
                                    style = "flex: 1; min-width: 0; overflow: hidden;"
                                    if (item.note.isNotEmpty()) {
                                        div {
                                            style = "font-size: 13px; color: ${t.textPrimary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                                            +item.note
                                        }
                                    }
                                    div {
                                        style = "font-size: 11px; color: ${t.textSecondary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                                        val primaryFmt = utils.CategoryUtils.formatCategoryNameLocalized(item.primaryCategory)
                                        val subFmt = utils.CategoryUtils.formatCategoryNameLocalized(item.subCategory)
                                        +"$primaryFmt → $subFmt"
                                    }
                                }
                                // Credit badge
                                if (isCredit) {
                                    span {
                                        style = "font-size: 10px; font-weight: 700; color: #4CAF50; background: #4CAF5022; padding: 2px 6px; border-radius: 8px; flex-shrink: 0;"
                                        +"CREDIT"
                                    }
                                }
                            }
                        } else {

                        div {
                            style = """
                                background: ${t.iconCircleDefault};
                                border-radius: 12px;
                                padding: 16px;
                                margin-bottom: 12px;
                            """

                            // Header with item number, credit badge, and delete button
                            div {
                                style = "display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;"
                                div {
                                    style = "display: flex; align-items: center; gap: 8px;"
                                    span {
                                        style = "font-weight: 600; color: ${t.textPrimary};"
                                        +"${StringKey.TRANSACTION_SPLIT_ITEM.tr()} ${index + 1}"
                                    }
                                    if (isCredit) {
                                        span {
                                            style = "font-size: 11px; font-weight: 700; color: #4CAF50; background: #4CAF5022; padding: 2px 7px; border-radius: 10px; letter-spacing: 0.5px;"
                                            +"CREDIT"
                                        }
                                    }
                                }
                                if (splitItems.size > 1) {
                                    button {
                                        style = """
                                            padding: 6px 10px;
                                            background: #ff6b6b20;
                                            border: 1px solid #ff6b6b;
                                            border-radius: 6px;
                                            color: #ff6b6b;
                                            cursor: pointer;
                                            font-size: 12px;
                                        """
                                        onClickFunction = {
                                            splitItems.removeAt(index)
                                            updateModalContent()
                                        }
                                        +"🗑 ${StringKey.TRANSACTION_SPLIT_REMOVE.tr()}"
                                    }
                                }
                            }

                            // Amount field
                            div {
                                style = "margin-bottom: 12px;"
                                label {
                                    style = "display: block; color: ${if (isCredit) "#4CAF50" else t.textSecondary}; font-size: 12px; margin-bottom: 4px;"
                                    +if (isCredit) StringKey.TRANSACTION_SPLIT_AMOUNT_CREDIT.tr() else StringKey.TRANSACTION_SPLIT_AMOUNT.tr()
                                }
                                input {
                                    type = InputType.number
                                    placeholder = "0.00"
                                    value = kotlin.math.abs(item.amount).let { if (it > 0) it.toString() else "" }
                                    style = """
                                        width: 100%;
                                        padding: 10px;
                                        border: 1px solid ${if (isCredit) "#4CAF5066" else t.border};
                                        border-radius: 8px;
                                        background: ${t.card};
                                        color: ${if (isCredit) "#4CAF50" else "white"};
                                        font-size: 16px;
                                        box-sizing: border-box;
                                    """
                                    onInputFunction = { e ->
                                        val value = (e.target as? org.w3c.dom.HTMLInputElement)?.value?.toDoubleOrNull() ?: 0.0
                                        handleAmountUpdate(index, value)
                                        // Update remaining amount display immediately
                                        updateRemainingDisplay()
                                    }
                                    // NOTE: Removed onBlurFunction - it was causing issues where
                                    // updateModalContent() would rebuild the DOM during button click,
                                    // destroying the button and preventing the click from registering
                                    onKeyDownFunction = { e ->
                                        val keyEvent = e as? org.w3c.dom.events.KeyboardEvent
                                        if (keyEvent?.key == "Enter") {
                                            e.preventDefault()
                                            // Add next split with remaining amount and refresh
                                            if (addNextSplitItem()) {
                                                updateModalContent()
                                            }
                                        }
                                    }
                                }
                            }

                            // Primary Category dropdown
                            div {
                                style = "margin-bottom: 12px;"
                                label {
                                    style = "display: block; color: ${t.textSecondary}; font-size: 12px; margin-bottom: 4px;"
                                    +StringKey.TRANSACTION_CATEGORY.tr()
                                }
                                select {
                                    style = """
                                        width: 100%;
                                        padding: 10px;
                                        border: 1px solid ${t.border};
                                        border-radius: 8px;
                                        background: ${t.card};
                                        color: ${t.textPrimary};
                                        font-size: 14px;
                                        box-sizing: border-box;
                                        cursor: pointer;
                                    """
                                    onChangeFunction = { e ->
                                        val value = (e.target as? org.w3c.dom.HTMLSelectElement)?.value ?: "Other"
                                        val current = splitItems[index] // Read current state, not stale snapshot
                                        val customSubs = customCategories.filter { it.primaryName.equals(value, ignoreCase = true) }.map { it.secondaryName }
                                        val newSubs = (getSharedSubcategories(value) + customSubs).distinct()
                                        val newSub = if (newSubs.contains(current.subCategory)) current.subCategory else newSubs.firstOrNull() ?: "Miscellaneous"
                                        splitItems[index] = current.copy(primaryCategory = value, subCategory = newSub)
                                        updateModalContent()
                                    }
                                    val allPrimaries = (getSharedPrimaryCategories() + customCategories.map { it.primaryName }).distinct().sorted()
                                    allPrimaries.forEach { category ->
                                        option {
                                            value = category
                                            selected = (item.primaryCategory == category)
                                            +utils.CategoryUtils.formatCategoryNameLocalized(category)
                                        }
                                    }
                                }
                            }

                            // Subcategory dropdown
                            div {
                                style = "margin-bottom: 12px;"
                                label {
                                    style = "display: block; color: ${t.textSecondary}; font-size: 12px; margin-bottom: 4px;"
                                    +StringKey.TRANSACTION_LABEL_SUBCATEGORY.tr()
                                }
                                val customSubsForPrimary = customCategories.filter { it.primaryName.equals(item.primaryCategory, ignoreCase = true) }.map { it.secondaryName }
                                val availableSubs = (getSharedSubcategories(item.primaryCategory) + customSubsForPrimary).distinct()
                                select {
                                    style = """
                                        width: 100%;
                                        padding: 10px;
                                        border: 1px solid ${t.border};
                                        border-radius: 8px;
                                        background: ${t.card};
                                        color: #ccc;
                                        font-size: 14px;
                                        box-sizing: border-box;
                                        cursor: pointer;
                                    """
                                    onChangeFunction = { e ->
                                        val value = (e.target as? org.w3c.dom.HTMLSelectElement)?.value ?: "Miscellaneous"
                                        splitItems[index] = splitItems[index].copy(subCategory = value)
                                        updateModalContent()
                                    }
                                    availableSubs.forEach { subcat ->
                                        option {
                                            value = subcat
                                            selected = (item.subCategory == subcat)
                                            val suffix = subcat.removePrefix("${item.primaryCategory}_")
                                            val subDisplayName = if (suffix == subcat) utils.CategoryUtils.formatCategoryNameLocalized(subcat)
                                                else utils.CategoryUtils.formatCategoryNameLocalized(suffix)
                                            +subDisplayName
                                        }
                                    }
                                }
                            }

                            // Note field
                            div {
                                label {
                                    style = "display: block; color: ${t.textSecondary}; font-size: 12px; margin-bottom: 4px;"
                                    +StringKey.TRANSACTION_SPLIT_NOTE_OPTIONAL.tr()
                                }
                                input {
                                    type = InputType.text
                                    placeholder = StringKey.TRANSACTION_SPLIT_NOTE_PLACEHOLDER.tr()
                                    value = item.note
                                    style = """
                                        width: 100%;
                                        padding: 10px;
                                        border: 1px solid ${t.border};
                                        border-radius: 8px;
                                        background: ${t.card};
                                        color: ${t.textPrimary};
                                        font-size: 14px;
                                        box-sizing: border-box;
                                    """
                                    onInputFunction = { e ->
                                        val value = (e.target as? org.w3c.dom.HTMLInputElement)?.value ?: ""
                                        splitItems[index] = splitItems[index].copy(note = value)
                                    }
                                }
                            }
                        }
                        } // end else (full form)
                    }

                    // Add Split Item button
                    val remaining = getRemainingAmount()
                    if (remaining > 0.01) {
                        button {
                            style = """
                                width: 100%;
                                padding: 12px;
                                background: transparent;
                                border: 2px dashed #4CAF50;
                                border-radius: 12px;
                                color: #4CAF50;
                                font-size: 16px;
                                font-weight: 500;
                                cursor: pointer;
                                margin-bottom: 12px;
                                transition: all 0.2s ease;
                            """
                            onMouseOver = "this.style.backgroundColor='#4CAF5020'"
                            onMouseOut = "this.style.backgroundColor='transparent'"
                            onClickFunction = {
                                if (addNextSplitItem()) {
                                    updateModalContent()
                                }
                            }
                            +"${StringKey.TRANSACTION_SPLIT_ADD_ITEM.tr()} (${formatCurrency(remaining)} ${StringKey.TRANSACTION_GOAL_REMAINING.tr()})"
                        }
                    }
                } else {
                    // CSV mode
                    p {
                        style = "color: ${t.textSecondary}; font-size: 14px; margin: 0 0 4px 0;"
                        +"Paste a list of transactions, or click 'Import CSV File' below"
                    }
                    p {
                        style = "color: ${t.textSecondary}; font-size: 11px; margin: 0 0 8px 0;"
                        +"Formats: amount,description OR description,amount (comma/tab/pipe)"
                    }
                    textArea {
                        style = """
                            width: 100%;
                            height: 150px;
                            padding: 12px;
                            border: 1px solid ${t.border};
                            border-radius: 8px;
                            background: ${t.card};
                            color: ${t.textPrimary};
                            font-family: monospace;
                            font-size: 14px;
                            resize: vertical;
                            box-sizing: border-box;
                            margin-bottom: 12px;
                        """
                        placeholder = "25.50\tGABRIEL PIZZA 152\n19.65\tHOTEL LE GERMAIN\n158.2\tCLAUDE.AI SUBSCRIPTION"
                        onInputFunction = { e ->
                            csvText = (e.target as? org.w3c.dom.HTMLTextAreaElement)?.value ?: ""
                            val hasText = csvText.isNotEmpty()
                            document.getElementById("csv-import-btn-${transaction.id}")?.asDynamic()?.style?.display = if (hasText) "none" else "block"
                            document.getElementById("csv-parse-btn-${transaction.id}")?.asDynamic()?.style?.display = if (hasText) "block" else "none"
                            // Clear error on any text change
                            csvParseError = null
                            document.getElementById("csv-error-msg-${transaction.id}")?.asDynamic()?.style?.display = "none"
                        }
                        +csvText
                    }

                    // Error message display — always in DOM so we can clear it without re-render
                    p {
                        id = "csv-error-msg-${transaction.id}"
                        style = """
                            display: ${if (csvParseError != null) "block" else "none"};
                            color: #FF6B6B;
                            font-size: 14px;
                            margin: 0 0 12px 0;
                            padding: 8px;
                            background: rgba(255, 107, 107, 0.1);
                            border-radius: 4px;
                        """
                        +(csvParseError ?: "")
                    }

                    // Import CSV File button
                    input {
                        type = InputType.file
                        accept = ".csv,.txt,text/csv,text/plain"
                        style = "display: none;"
                        id = "csv-file-input-${transaction.id}"
                        onChangeFunction = { e ->
                            val fileInput = e.target as? org.w3c.dom.HTMLInputElement
                            val file = fileInput?.files?.item(0)
                            if (file != null) {
                                val reader = org.w3c.files.FileReader()
                                reader.onload = { event ->
                                    val result = event.target.asDynamic().result as? String
                                    if (result != null) {
                                        // Show loading state immediately
                                        csvParseError = null
                                        csvText = "AI is categorizing your transactions..."
                                        updateModalContent()
                                        kotlinx.coroutines.MainScope().launch {
                                            val items = parseCsvAndCategorize(result)
                                            if (items == null) {
                                                csvParseError = "Could not parse CSV file. Ensure it has amount and description columns."
                                                csvText = ""
                                                updateModalContent()
                                            } else {
                                                splitItems.clear()
                                                splitItems.addAll(items)
                                                csvText = ""
                                                inputMode = "manual"
                                                isFromCsv = true
                                                updateModalContent()
                                            }
                                        }
                                    } else {
                                        csvParseError = "File is empty or could not be read."
                                        updateModalContent()
                                    }
                                }
                                reader.onerror = {
                                    csvParseError = "Failed to read file."
                                    updateModalContent()
                                    Unit
                                }
                                reader.readAsText(file as org.w3c.files.Blob)
                            }
                        }
                    }
                    button {
                        id = "csv-import-btn-${transaction.id}"
                        style = """
                            display: block;
                            width: 100%;
                            padding: 12px;
                            background: ${t.iconCircleDefault};
                            border: 1px solid #2196F3;
                            border-radius: 8px;
                            color: #2196F3;
                            font-weight: 600;
                            cursor: pointer;
                            margin-bottom: 12px;
                        """
                        onMouseOver = "this.style.backgroundColor='#2196F3'; this.style.color='${if (t.isDark) "#000" else "#fff"}'"
                        onMouseOut = "this.style.backgroundColor='${t.iconCircleDefault}'; this.style.color='#2196F3'"
                        onClickFunction = {
                            document.getElementById("csv-file-input-${transaction.id}")?.asDynamic()?.click()
                        }
                        +"📁 Import CSV File"
                    }

                    button {
                        id = "csv-parse-btn-${transaction.id}"
                        style = """
                            display: none;
                            width: 100%;
                            padding: 12px;
                            background: ${t.accent};
                            border: none;
                            border-radius: 8px;
                            color: #000;
                            font-weight: 600;
                            cursor: pointer;
                            margin-bottom: 16px;
                        """
                        onClickFunction = {
                            csvParseError = null
                            val textToParse = csvText.trim()
                            if (textToParse.isEmpty()) {
                                csvParseError = "No data to parse. Please enter transaction data."
                                updateModalContent()
                            } else {
                                csvText = "AI is categorizing your transactions..."
                                updateModalContent()
                                kotlinx.coroutines.MainScope().launch {
                                    try {
                                        val items = parseCsvAndCategorize(textToParse)
                                        if (items == null) {
                                            csvParseError = "Could not parse. Please check the format: amount and description per line."
                                            csvText = textToParse
                                            updateModalContent()
                                        } else {
                                            splitItems.clear()
                                            splitItems.addAll(items)
                                            csvText = ""
                                            inputMode = "manual"
                                            isFromCsv = true
                                            updateModalContent()
                                        }
                                    } catch (e: Exception) {
                                        csvParseError = "Failed to parse: ${e.message}"
                                        csvText = textToParse
                                        updateModalContent()
                                    }
                                }
                            }
                        }
                        +"Parse & Categorize"
                    }
                }

                // Save button — only shown when >= 2 items and amounts balance to parent total
                val itemsWithAmount = splitItems.filter { kotlin.math.abs(it.amount) > 0.01 }
                val remaining = getRemainingAmount()
                val isBalanced = kotlin.math.abs(remaining) < 0.01
                if (itemsWithAmount.size >= 2) {
                    if (!isBalanced) {
                        p {
                            style = """
                                color: #FF9500;
                                font-size: 13px;
                                margin: 0 0 8px 0;
                                padding: 8px 12px;
                                background: rgba(255, 149, 0, 0.1);
                                border-radius: 6px;
                                text-align: center;
                            """
                            +"⚠ ${formatCurrency(kotlin.math.abs(remaining))} ${if (remaining > 0) StringKey.TRANSACTION_SPLIT_REMAINING_WARNING.tr() else StringKey.TRANSACTION_SPLIT_OVER_WARNING.tr()}"
                        }
                    } else {
                        button {
                            style = """
                                width: 100%;
                                padding: 14px;
                                background: #4CAF50;
                                border: none;
                                border-radius: 8px;
                                color: ${t.textPrimary};
                                font-weight: 600;
                                font-size: 16px;
                                cursor: pointer;
                            """
                            onClickFunction = {
                                val validSplits = splitItems.filter { kotlin.math.abs(it.amount) > 0.01 }
                                onSplitTransaction?.invoke(validSplits)
                                document.body?.removeChild(modalElement)
                            }
                            +"Save Split"
                        }
                    }
                }
            }

            modalElement.appendChild(modalContent)
        }

        // Load custom categories for split category dropdowns
        if (apiService != null) {
            MainScope().launch {
                customCategories = try { apiService.getCustomCategories() } catch (e: Exception) { emptyList() }
                updateModalContent()
            }
        }

        updateModalContent()
        document.body?.appendChild(modalElement)
    }

    // MARK: - CSV File Parsing Helpers

    /**
     * Parses a CSV file and returns (amount, description) pairs for AI categorization.
     * Negative amounts represent credits/refunds (they reduce the split total).
     * Reuses the same delimiter/header detection as parseAndFormatCsvFile.
     */
    private fun parseCsvToRows(fileContent: String): List<Pair<Double, String>>? {
        return try {
            val formatted = parseAndFormatCsvFile(fileContent) ?: return null
            formatted.trim().split("\n")
                .filter { it.isNotBlank() }
                .mapNotNull { line ->
                    val parts = line.split("\t")
                    val amount = parts.getOrNull(0)?.trim()?.toDoubleOrNull() ?: return@mapNotNull null
                    if (amount == 0.0) return@mapNotNull null  // allow negatives (credits)
                    val description = parts.getOrNull(1)?.trim() ?: return@mapNotNull null
                    if (description.isBlank()) return@mapNotNull null
                    Pair(amount, description)
                }
                .takeIf { it.isNotEmpty() }
        } catch (e: Exception) {
            null
        }
    }

    /**
     * Parses a CSV file and formats it as "amount\tdescription" pairs
     * Intelligently detects and skips date columns
     * Returns null if parsing fails or produces no results
     */
    private fun parseAndFormatCsvFile(fileContent: String): String? {
        return try {
            val lines = fileContent.trim().split("\n").filter { it.isNotBlank() }
            if (lines.isEmpty()) return null

            // Smart delimiter detection
            val firstLine = lines[0]
            val delimiter = detectDelimiter(firstLine)

            // Check if first line is a header
            val hasHeader = firstLine.lowercase().let {
                it.contains("date") || it.contains("amount") || it.contains("description") ||
                it.contains("merchant") || it.contains("transaction") || it.contains("name")
            }

            val result = if (hasHeader && lines.size > 1) {
                parseWithHeader(lines, delimiter)
            } else {
                parseWithoutHeader(lines, delimiter)
            }

            // Validate we got some results
            if (result.isNotBlank()) result else null
        } catch (e: Exception) {
            console.log("CSV parsing error: ${e.message}")
            null
        }
    }

    /**
     * Smart delimiter detection supporting:
     * - Tab-delimited
     * - Pipe-delimited
     * - Semicolon-delimited
     * - Comma-delimited
     * - Whitespace-delimited (2+ spaces or tabs between columns)
     */
    private fun detectDelimiter(line: String): String {
        return when {
            line.contains("\t") -> "\t"
            line.contains("|") -> "|"
            line.contains(";") -> ";"
            line.contains(",") -> ","
            Regex("""\s{2,}""").containsMatchIn(line) -> "WHITESPACE"
            else -> ","
        }
    }

    /**
     * Split a line by delimiter, handling whitespace delimiter specially.
     * Empty fields are filtered out — use this for heuristic description detection.
     */
    private fun splitByDelimiter(line: String, delimiter: String): List<String> {
        return if (delimiter == "WHITESPACE") {
            line.split(Regex("""\s{2,}""")).map { it.trim() }.filter { it.isNotEmpty() }
        } else {
            line.split(delimiter).map { it.trim() }.filter { it.isNotEmpty() }
        }
    }

    /**
     * Split a line preserving empty fields so that column indices stay stable.
     * Required for index-based access (debit/credit column detection and header-mapped columns).
     */
    private fun splitByDelimiterRaw(line: String, delimiter: String): List<String> {
        return if (delimiter == "WHITESPACE") {
            line.split(Regex("""\s{2,}""")).map { it.trim() }
        } else {
            line.split(delimiter).map { it.trim() }
        }
    }

    /**
     * Parse CSV with header row to identify amount and description columns.
     * Detects separate Debit/Credit columns: debit → positive, credit → negative.
     */
    private fun parseWithHeader(lines: List<String>, delimiter: String): String {
        val headers = splitByDelimiter(lines[0], delimiter).map { it.lowercase() }

        // Detect separate debit/credit columns
        val debitIndex = headers.indexOfFirst {
            it.contains("debit") || it.contains("withdrawal") || it.contains("charge")
        }
        val creditIndex = headers.indexOfFirst {
            it.contains("credit") || it.contains("deposit") || it.contains("refund")
        }
        val hasSplitAmounts = debitIndex >= 0 && creditIndex >= 0

        // Find single amount column (when no separate debit/credit)
        val amountIndex = if (hasSplitAmounts) -1 else headers.indexOfFirst {
            it.contains("amount") || it.contains("price") || it.contains("total") || it.contains("value")
        }

        // Find description column index
        val descriptionIndex = headers.indexOfFirst {
            it.contains("description") || it.contains("merchant") || it.contains("name") ||
            it.contains("transaction") || it.contains("payee")
        }

        // If we can't find anything useful, fall back to heuristic parsing
        if (!hasSplitAmounts && amountIndex == -1 && descriptionIndex == -1) {
            return parseWithoutHeader(lines.drop(1), delimiter)
        }

        val result = mutableListOf<String>()
        for (i in 1 until lines.size) {
            // Use raw split (preserving empty fields) so header-mapped column indices stay correct.
            // A credit row like "date,desc,,20.00" must keep col 2 empty, col 3 = 20.00.
            val parts = splitByDelimiterRaw(lines[i], delimiter)

            val amount = when {
                hasSplitAmounts -> {
                    val debit = if (debitIndex < parts.size) parseAmountFromString(parts[debitIndex]) else 0.0
                    val credit = if (creditIndex < parts.size) parseAmountFromString(parts[creditIndex]) else 0.0
                    when {
                        debit > 0.0 -> debit       // expense
                        credit > 0.0 -> -credit    // credit/refund stored as negative
                        else -> 0.0
                    }
                }
                amountIndex >= 0 && amountIndex < parts.size ->
                    parseAmountFromString(parts[amountIndex])
                else ->
                    parts.firstNotNullOfOrNull { parseAmountFromString(it).takeIf { it > 0.0 } } ?: 0.0
            }

            val description = if (descriptionIndex >= 0 && descriptionIndex < parts.size) {
                parts[descriptionIndex]
            } else {
                // Skip empty fields when searching heuristically for the description
                parts.firstOrNull { part ->
                    part.isNotBlank() && parseAmountFromString(part) == 0.0 && !isLikelyDate(part)
                } ?: ""
            }

            if (amount != 0.0 && description.isNotEmpty()) {
                result.add("$amount\t$description")
            }
        }

        return result.joinToString("\n")
    }

    /**
     * Detects whether a headerless CSV uses separate debit/credit columns.
     * Returns (debitColIndex, creditColIndex) in the FULL (non-date-filtered) row if detected,
     * null if it's a single-amount-column format.
     *
     * The signal: two adjacent numeric column positions where each row populates at most one
     * of them — the classic bank statement "Withdrawals / Deposits" layout.
     */
    private fun detectDebitCreditColumns(lines: List<String>, delimiter: String): Pair<Int, Int>? {
        if (lines.size < 2) return null
        // Use raw split to preserve empty fields — mutual exclusivity requires stable column indices.
        val rows = lines.map { splitByDelimiterRaw(it, delimiter) }
        val maxCols = rows.maxOfOrNull { it.size } ?: return null
        if (maxCols < 3) return null  // need at least 3 cols (date, description, debit/credit) to be meaningful

        // Count rows that have a non-zero numeric value in each column
        val colNumericCount = IntArray(maxCols)
        for (row in rows) {
            for (i in row.indices) {
                if (!isLikelyDate(row[i]) && parseAmountFromString(row[i]) > 0) colNumericCount[i]++
            }
        }

        // Find columns that independently contain numeric values (excludes date/description cols)
        val numericCols = colNumericCount.indices.filter { colNumericCount[it] > 0 }
        if (numericCols.size < 2) return null

        // Test ALL pairs of numeric columns and pick the one with the lowest mutual-occurrence ratio.
        // The genuine debit/credit pair is never simultaneously filled (ratio ≈ 0), so it always wins
        // over any pair that includes a running-balance column (which always co-occurs with amounts).
        var bestScore = 0.10  // pair must beat this threshold to be considered
        var bestPair: Pair<Int, Int>? = null

        for (i in numericCols.indices) {
            for (j in i + 1 until numericCols.size) {
                val colA = numericCols[i]
                val colB = numericCols[j]
                var bothFilled = 0
                var eitherFilled = 0
                for (row in rows) {
                    val a = if (colA < row.size) parseAmountFromString(row[colA]) else 0.0
                    val b = if (colB < row.size) parseAmountFromString(row[colB]) else 0.0
                    if (a > 0 || b > 0) eitherFilled++
                    if (a > 0 && b > 0) bothFilled++
                }
                if (eitherFilled >= 2) {
                    val score = bothFilled.toDouble() / eitherFilled
                    if (score < bestScore) {
                        bestScore = score
                        bestPair = Pair(colA, colB)
                    }
                }
            }
        }

        return bestPair
    }

    /**
     * Detects a running-balance column paired with an amount column.
     * A balance column: (1) has a numeric value in ≥90% of rows, (2) consecutive differences
     * between adjacent row balances approximately equal the amount in the paired column for
     * ≥50% of verifiable row pairs.
     *
     * Returns Pair(amountColIndex, balanceColIndex) or null if not detected.
     */
    private fun detectAmountAndBalanceColumns(rows: List<List<String>>, numericCols: List<Int>): Pair<Int, Int>? {
        if (rows.size < 3 || numericCols.size < 2) return null

        for (balIdx in numericCols.indices) {
            val balCol = numericCols[balIdx]
            // Balance column must be populated in ≥90% of rows
            val coverage = rows.count { row ->
                balCol < row.size && parseAmountFromString(row[balCol]) > 0
            }.toDouble() / rows.size
            if (coverage < 0.9) continue

            for (amtIdx in numericCols.indices) {
                if (amtIdx == balIdx) continue
                val amtCol = numericCols[amtIdx]

                // Verify: |balance[i] − balance[i+1]| ≈ amount[i] for ≥50% of adjacent pairs
                var matchCount = 0
                var totalVerifiable = 0
                for (i in 0 until rows.size - 1) {
                    val amt = if (amtCol < rows[i].size) parseAmountFromString(rows[i][amtCol]) else 0.0
                    val bal = if (balCol < rows[i].size) parseAmountFromString(rows[i][balCol]) else 0.0
                    val nextBal = if (balCol < rows[i + 1].size) parseAmountFromString(rows[i + 1][balCol]) else 0.0
                    if (amt > 0 && bal > 0 && nextBal > 0) {
                        totalVerifiable++
                        if (kotlin.math.abs(kotlin.math.abs(bal - nextBal) - amt) < 0.02) matchCount++
                    }
                }

                if (totalVerifiable >= 2 && matchCount.toDouble() / totalVerifiable >= 0.5) {
                    return Pair(amtCol, balCol)
                }
            }
        }
        return null
    }

    /**
     * Parse CSV without header - use heuristics to identify amount and description.
     * Detects debit/credit column pattern automatically.
     * Falls back to balance-delta signing when a running-balance column is present.
     */
    private fun parseWithoutHeader(lines: List<String>, delimiter: String): String {
        val result = mutableListOf<String>()
        val debitCreditCols = detectDebitCreditColumns(lines, delimiter)

        // If no explicit debit/credit pair, try to detect a running-balance column and use
        // consecutive balance deltas to sign amounts (positive delta = debit, negative = credit).
        val rows = if (debitCreditCols == null) lines.map { splitByDelimiterRaw(it, delimiter) } else null
        val balancePair: Pair<Int, Int>? = if (rows != null) {
            val maxCols = rows.maxOfOrNull { it.size } ?: 0
            val colNumericCount = IntArray(maxCols)
            for (row in rows) {
                for (i in row.indices) {
                    if (!isLikelyDate(row[i]) && parseAmountFromString(row[i]) > 0) colNumericCount[i]++
                }
            }
            val numericCols = colNumericCount.indices.filter { colNumericCount[it] > 0 }
            detectAmountAndBalanceColumns(rows, numericCols)
        } else null

        for ((lineIndex, line) in lines.withIndex()) {
            // Raw split needed when column indices are significant (debit/credit or balance pair).
            val parts = if (debitCreditCols != null || balancePair != null) splitByDelimiterRaw(line, delimiter)
                        else splitByDelimiter(line, delimiter)
            if (parts.isEmpty()) continue

            val amount: Double
            if (debitCreditCols != null) {
                val (debitCol, creditCol) = debitCreditCols
                val debit = if (debitCol < parts.size) parseAmountFromString(parts[debitCol]) else 0.0
                val credit = if (creditCol < parts.size) parseAmountFromString(parts[creditCol]) else 0.0
                amount = when {
                    debit > 0.0 -> debit       // expense → positive
                    credit > 0.0 -> -credit    // refund/credit → negative
                    else -> 0.0
                }
            } else if (balancePair != null) {
                val (amtCol, balCol) = balancePair
                val amt = if (amtCol < parts.size) parseAmountFromString(parts[amtCol]) else 0.0
                if (amt <= 0.0) {
                    amount = 0.0
                } else {
                    // delta = balance[current] − balance[next in file]
                    // Most bank/card CSVs are newest-first: positive delta → debit, negative → credit
                    val bal = if (balCol < parts.size) parseAmountFromString(parts[balCol]) else 0.0
                    val nextParts = if (lineIndex + 1 < lines.size)
                        splitByDelimiterRaw(lines[lineIndex + 1], delimiter) else emptyList()
                    val nextBal = if (balCol < nextParts.size) parseAmountFromString(nextParts[balCol]) else 0.0
                    amount = when {
                        bal > 0 && nextBal > 0 && kotlin.math.abs(kotlin.math.abs(bal - nextBal) - amt) < 0.02 ->
                            if (bal > nextBal) amt else -amt   // bal↓ = credit, bal↑ = debit
                        else -> amt   // last row or ambiguous → default to debit
                    }
                }
            } else {
                val nonDateParts = parts.filter { !isLikelyDate(it) }
                amount = nonDateParts.firstNotNullOfOrNull {
                    parseAmountFromString(it).takeIf { it > 0.0 }
                } ?: 0.0
            }

            // Find description (first non-empty, non-numeric, non-date field)
            val description = parts.firstOrNull {
                it.isNotBlank() && parseAmountFromString(it) == 0.0 && !isLikelyDate(it)
            } ?: ""

            if (amount != 0.0 && description.isNotEmpty()) {
                result.add("$amount\t$description")
            }
        }

        return result.joinToString("\n")
    }

    /**
     * Check if a string is likely a date
     */
    private fun isLikelyDate(str: String): Boolean {
        val datePatterns = listOf(
            Regex("""\d{1,2}/\d{1,2}/\d{2,4}"""),          // MM/DD/YYYY or DD/MM/YYYY
            Regex("""\d{1,2}-\d{1,2}-\d{2,4}"""),          // MM-DD-YYYY or DD-MM-YYYY
            Regex("""\d{4}-\d{1,2}-\d{1,2}"""),            // YYYY-MM-DD
            Regex("""\d{4}/\d{1,2}/\d{1,2}"""),            // YYYY/MM/DD
            Regex("""\w{3}\s+\d{1,2},?\s+\d{4}"""),        // Jan 15, 2025 or Jan 15 2025
            Regex("""\d{1,2}\s+\w{3}\s+\d{4}"""),          // 15 Jan 2025
            Regex("""\d{8}""")                              // YYYYMMDD
        )

        return datePatterns.any { it.matches(str) }
    }

    /**
     * Parse amount from string, removing currency symbols and commas
     */
    private fun parseAmountFromString(str: String): Double {
        val cleaned = str.replace("$", "").replace(",", "")
        return cleaned.toDoubleOrNull() ?: 0.0
    }

    private fun showDistributeModal() {
        val t = WebTheme.current
        val parentAmount = kotlin.math.abs(transaction.amount)
        val parentDate = transaction.date
        val parentYear = parentDate.getFullYear()
        val parentMonth = parentDate.getMonth() // 0-indexed

        // State
        var startYear = parentYear
        var startMonth = parentMonth
        var endYear = parentYear
        var endMonth = parentMonth

        fun getMonthCount(): Int {
            return (endYear - startYear) * 12 + (endMonth - startMonth) + 1
        }

        fun getDistributedAmount(): Double = kotlin.math.round(parentAmount / getMonthCount() * 100.0) / 100.0

        fun formatMonth(year: Int, month: Int): String {
            val months = arrayOf(
                StringKey.MONTH_JANUARY.tr(), StringKey.MONTH_FEBRUARY.tr(), StringKey.MONTH_MARCH.tr(),
                StringKey.MONTH_APRIL.tr(), StringKey.MONTH_MAY_FULL.tr(), StringKey.MONTH_JUNE.tr(),
                StringKey.MONTH_JULY.tr(), StringKey.MONTH_AUGUST.tr(), StringKey.MONTH_SEPTEMBER.tr(),
                StringKey.MONTH_OCTOBER.tr(), StringKey.MONTH_NOVEMBER.tr(), StringKey.MONTH_DECEMBER.tr()
            )
            return "${months[month]} $year"
        }

        fun canGoStartBack(): Boolean = true // No lower limit
        fun canGoStartForward(): Boolean {
            // Start can advance up to end month (allows single-month "move")
            val endMonthNum = endYear * 12 + endMonth
            val startMonthNum = startYear * 12 + startMonth
            return startMonthNum < endMonthNum
        }

        fun canGoEndBack(): Boolean {
            // End can go back to start month (allows single-month "move")
            val startMonthNum = startYear * 12 + startMonth
            val endMonthNum = endYear * 12 + endMonth
            return endMonthNum > startMonthNum
        }

        fun canGoEndForward(): Boolean = true // No upper limit

        // Create modal
        val modalElement = document.createElement("div") as HTMLElement
        modalElement.style.cssText = """
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        """
        modalElement.addEventListener("click", { event ->
            if (event.target == modalElement) document.body?.removeChild(modalElement)
        })

        fun updateModalContent() {
            modalElement.innerHTML = ""

            val modalContent = document.create.div {
                style = """
                    background: ${t.card};
                    border-radius: 16px;
                    padding: 24px;
                    max-width: 500px;
                    width: 90%;
                    max-height: 80vh;
                    overflow-y: auto;
                    color: ${t.textPrimary};
                """

                // Header
                div {
                    style = "display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;"
                    h3 {
                        style = "font-size: 24px; font-weight: 600; margin: 0;"
                        +(if (getMonthCount() == 1) StringKey.TRANSACTION_MOVE.tr() else StringKey.TRANSACTION_DISTRIBUTE.tr())
                    }
                    button {
                        style = "background: none; border: none; color: ${t.textPrimary}; font-size: 24px; cursor: pointer;"
                        onClickFunction = { document.body?.removeChild(modalElement) }
                        +"×"
                    }
                }

                p {
                    style = "color: ${t.textSecondary}; margin: 0 0 24px 0;"
                    +"${StringKey.TRANSACTION_DISTRIBUTE_TOTAL.tr()} ${formatCurrency(parentAmount)}"
                }

                // Start month selector
                div {
                    style = "display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;"
                    span { style = "color: ${t.textSecondary};"; +StringKey.TRANSACTION_DISTRIBUTE_START_MONTH.tr() }
                    div {
                        style = "display: flex; align-items: center; gap: 8px;"
                        button {
                            style = """
                                width: 36px;
                                height: 36px;
                                border-radius: 50%;
                                border: 1px solid ${if (canGoStartBack()) t.accent else t.border};
                                background: transparent;
                                color: ${if (canGoStartBack()) t.accent else t.textSecondary};
                                cursor: ${if (canGoStartBack()) "pointer" else "not-allowed"};
                                font-size: 18px;
                            """
                            disabled = !canGoStartBack()
                            onClickFunction = {
                                if (canGoStartBack()) {
                                    if (startMonth == 0) {
                                        startMonth = 11
                                        startYear--
                                    } else {
                                        startMonth--
                                    }
                                    updateModalContent()
                                }
                            }
                            +"‹"
                        }
                        span {
                            style = "min-width: 130px; text-align: center; font-weight: 500;"
                            +formatMonth(startYear, startMonth)
                        }
                        button {
                            style = """
                                width: 36px;
                                height: 36px;
                                border-radius: 50%;
                                border: 1px solid ${if (canGoStartForward()) t.accent else t.border};
                                background: transparent;
                                color: ${if (canGoStartForward()) t.accent else t.textSecondary};
                                cursor: ${if (canGoStartForward()) "pointer" else "not-allowed"};
                                font-size: 18px;
                            """
                            disabled = !canGoStartForward()
                            onClickFunction = {
                                if (canGoStartForward()) {
                                    if (startMonth == 11) {
                                        startMonth = 0
                                        startYear++
                                    } else {
                                        startMonth++
                                    }
                                    updateModalContent()
                                }
                            }
                            +"›"
                        }
                    }
                }

                // End month selector
                div {
                    style = "display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;"
                    span { style = "color: ${t.textSecondary};"; +StringKey.TRANSACTION_DISTRIBUTE_END_MONTH.tr() }
                    div {
                        style = "display: flex; align-items: center; gap: 8px;"
                        button {
                            style = """
                                width: 36px;
                                height: 36px;
                                border-radius: 50%;
                                border: 1px solid ${if (canGoEndBack()) t.accent else t.border};
                                background: transparent;
                                color: ${if (canGoEndBack()) t.accent else t.textSecondary};
                                cursor: ${if (canGoEndBack()) "pointer" else "not-allowed"};
                                font-size: 18px;
                            """
                            disabled = !canGoEndBack()
                            onClickFunction = {
                                if (canGoEndBack()) {
                                    if (endMonth == 0) {
                                        endMonth = 11
                                        endYear--
                                    } else {
                                        endMonth--
                                    }
                                    updateModalContent()
                                }
                            }
                            +"‹"
                        }
                        span {
                            style = "min-width: 130px; text-align: center; font-weight: 500;"
                            +formatMonth(endYear, endMonth)
                        }
                        button {
                            style = """
                                width: 36px;
                                height: 36px;
                                border-radius: 50%;
                                border: 1px solid ${if (canGoEndForward()) t.accent else t.border};
                                background: transparent;
                                color: ${if (canGoEndForward()) t.accent else t.textSecondary};
                                cursor: ${if (canGoEndForward()) "pointer" else "not-allowed"};
                                font-size: 18px;
                            """
                            disabled = !canGoEndForward()
                            onClickFunction = {
                                if (canGoEndForward()) {
                                    if (endMonth == 11) {
                                        endMonth = 0
                                        endYear++
                                    } else {
                                        endMonth++
                                    }
                                    updateModalContent()
                                }
                            }
                            +"›"
                        }
                    }
                }

                // Distributed amount display
                div {
                    style = """
                        background: #2196F320;
                        border: 1px solid #2196F3;
                        border-radius: 12px;
                        padding: 20px;
                        text-align: center;
                        margin-bottom: 20px;
                    """
                    p {
                        style = "color: ${t.textSecondary}; margin: 0 0 8px 0; font-size: 14px;"
                        +(if (getMonthCount() == 1) StringKey.TRANSACTION_MOVE_TO.tr() else StringKey.TRANSACTION_DISTRIBUTE_EACH_MONTH.tr())
                    }
                    p {
                        style = "color: #2196F3; font-size: 32px; font-weight: bold; margin: 0 0 4px 0;"
                        +formatCurrency(getDistributedAmount())
                    }
                    if (getMonthCount() > 1) {
                        p {
                            style = "color: ${t.textSecondary}; margin: 0; font-size: 14px;"
                            +"(${getMonthCount()} ${StringKey.TRANSACTION_DISTRIBUTE_MONTHS.tr()})"
                        }
                    }
                }

                // Preview list
                h4 {
                    style = "font-size: 16px; color: ${t.textSecondary}; margin: 0 0 12px 0;"
                    +(if (getMonthCount() == 1) StringKey.TRANSACTION_MOVE_PREVIEW.tr() else StringKey.TRANSACTION_DISTRIBUTE_PREVIEW.tr())
                }
                div {
                    style = "max-height: 150px; overflow-y: auto; margin-bottom: 20px;"
                    var y = startYear
                    var m = startMonth
                    while (y < endYear || (y == endYear && m <= endMonth)) {
                        val isParentMonth = (y == parentYear && m == parentMonth)
                        div {
                            style = """
                                display: flex;
                                justify-content: space-between;
                                padding: 10px 12px;
                                background: ${t.iconCircleDefault};
                                border-radius: 6px;
                                margin-bottom: 4px;
                                border-left: 3px solid ${if (isParentMonth) "#4CAF50" else "transparent"};
                            """
                            span {
                                style = "color: ${if (isParentMonth) "#4CAF50" else t.textPrimary};"
                                +formatMonth(y, m)
                            }
                            span {
                                style = "font-weight: 500; color: ${t.textPrimary};"
                                +formatCurrency(getDistributedAmount())
                            }
                        }
                        if (m == 11) {
                            m = 0
                            y++
                        } else {
                            m++
                        }
                    }
                }

                // Save button
                button {
                    style = """
                        width: 100%;
                        padding: 14px;
                        background: #2196F3;
                        border: none;
                        border-radius: 8px;
                        color: ${t.textPrimary};
                        font-weight: 600;
                        font-size: 16px;
                        cursor: pointer;
                    """
                    onClickFunction = {
                        val startMonthStr = "${startYear}-${(startMonth + 1).toString().padStart(2, '0')}"
                        val endMonthStr = "${endYear}-${(endMonth + 1).toString().padStart(2, '0')}"
                        // Call callback first, then remove overlay (same order as split)
                        onDistributeTransaction?.invoke(startMonthStr, endMonthStr)
                        document.body?.removeChild(modalElement)
                    }
                    +(if (getMonthCount() == 1) StringKey.TRANSACTION_MOVE.tr() else StringKey.TRANSACTION_DISTRIBUTE.tr())
                }
            }

            modalElement.appendChild(modalContent)
        }

        updateModalContent()
        document.body?.appendChild(modalElement)
    }
}

private fun showCategoryPreview(
    modalElement: org.w3c.dom.HTMLElement,
    sourceItem: CategoryPreviewItem,
    items: List<CategoryPreviewItem>,
    categoryCode: String,
    onCategoryChanged: ((String, Boolean, List<String>?) -> Unit)?
) {
    val t = WebTheme.current
    val contentDiv = modalElement.querySelector("div > div") as? org.w3c.dom.HTMLElement ?: return
    contentDiv.innerHTML = ""

    // Selection state: source is always selected; peers default to their `preselected` flag
    // (true when their current detailed category matches the source's). User can toggle.
    val selectedIds = mutableSetOf(sourceItem.transactionId)
    items.filter { it.preselected }.forEach { selectedIds.add(it.transactionId) }

    fun categoryLabel(detailed: String?): String {
        if (detailed.isNullOrBlank()) return ""
        // detailed is in the form "PRIMARY_FOO_BAR" — show the human-readable form
        return utils.CategoryUtils.formatTwoLevelCategoryLocalized(
            detailed.substringBefore("_"),
            detailed,
        )
    }

    lateinit var applyButton: org.w3c.dom.HTMLButtonElement
    fun refreshApplyLabel() {
        applyButton.textContent = StringKey.CATEGORY_PREVIEW_CONFIRM.tr().replace("%s", selectedIds.size.toString())
    }

    val previewContent = document.create.div {
        style = """
            background: ${t.card};
            border-radius: 16px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            max-height: 80vh;
            overflow-y: auto;
            border: 1px solid ${t.border};
        """
        h3 {
            style = "font-size: 18px; font-weight: 600; color: ${t.textPrimary}; margin: 0 0 16px 0; text-align: center;"
            +StringKey.CATEGORY_PREVIEW_TITLE.tr()
        }
        div {
            style = "display: flex; flex-direction: column; gap: 6px; max-height: 320px; overflow-y: auto; margin-bottom: 16px;"
            fun renderItem(item: CategoryPreviewItem, isSource: Boolean) {
                val formattedAmount = kotlin.math.abs(item.amount).let { amt ->
                    "$${amt.asDynamic().toFixed(2)}"
                }
                val initiallyChecked = isSource || item.transactionId in selectedIds
                val checkboxId = "cat-preview-cb-${item.transactionId}"
                label("") {
                    htmlFor = checkboxId
                    style = if (isSource) """
                        display: flex; align-items: center; gap: 10px;
                        padding: 10px 12px; background: rgba(0, 217, 255, 0.08); border-radius: 8px;
                        border: 1px solid rgba(0, 217, 255, 0.3); cursor: default;
                    """ else """
                        display: flex; align-items: center; gap: 10px;
                        padding: 10px 12px; background: ${t.border}; border-radius: 8px; cursor: pointer;
                    """
                    input(type = InputType.checkBox) {
                        id = checkboxId
                        style = "width: 18px; height: 18px; flex-shrink: 0; cursor: ${if (isSource) "default" else "pointer"}; accent-color: ${t.accent};"
                        checked = initiallyChecked
                        if (isSource) disabled = true
                        onChangeFunction = { event ->
                            val cb = event.target as org.w3c.dom.HTMLInputElement
                            if (cb.checked) selectedIds.add(item.transactionId) else selectedIds.remove(item.transactionId)
                            refreshApplyLabel()
                        }
                    }
                    div {
                        style = "flex: 1; min-width: 0;"
                        div {
                            style = "color: ${t.textPrimary}; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                            +item.name
                            if (isSource) {
                                span {
                                    style = "color: ${t.accent}; font-size: 11px; margin-left: 6px;"
                                    +"(current)"
                                }
                            }
                        }
                        div {
                            style = "color: ${t.textSecondary}; font-size: 12px; margin-top: 2px; display: flex; gap: 8px; flex-wrap: wrap;"
                            span { +item.date }
                            if (!isSource && !item.detailedCategory.isNullOrBlank()) {
                                span {
                                    style = "color: ${t.textSecondary}; opacity: 0.85;"
                                    +"• ${categoryLabel(item.detailedCategory)}"
                                }
                            }
                        }
                    }
                    div {
                        style = "color: ${t.textPrimary}; font-size: 14px; font-weight: 600; margin-left: 8px; white-space: nowrap;"
                        +formattedAmount
                    }
                }
            }
            renderItem(sourceItem, true)
            items.forEach { item -> renderItem(item, false) }
        }
        div {
            style = "display: flex; gap: 10px; margin-top: 8px;"
            button {
                style = """
                    flex: 1; padding: 14px; border-radius: 10px; cursor: pointer;
                    font-size: 15px; font-weight: 500;
                    background: ${t.border}; color: ${t.textPrimary}; border: 1px solid ${t.chipUnselectedBorder};
                """
                onClickFunction = {
                    document.body?.removeChild(modalElement)
                }
                +StringKey.ACTION_CANCEL.tr()
            }
            button {
                style = """
                    flex: 1; padding: 14px; border-radius: 10px; cursor: pointer;
                    font-size: 15px; font-weight: 600;
                    background: ${t.accent}; color: ${if (t.isDark) "#000" else "#fff"}; border: none;
                """
                onClickFunction = {
                    onCategoryChanged?.invoke(categoryCode, false, selectedIds.toList())
                    document.body?.removeChild(modalElement)
                }
                +StringKey.CATEGORY_PREVIEW_CONFIRM.tr().replace("%s", selectedIds.size.toString())
            }
        }
    }

    // Replace modal content
    contentDiv.parentElement?.replaceChild(previewContent, contentDiv)
    applyButton = previewContent.querySelectorAll("button")
        .let { it.item(it.length - 1) as org.w3c.dom.HTMLButtonElement }
    refreshApplyLabel()
}

// Helper data class for split items
data class SplitItemData(
    val amount: Double = 0.0,
    val primaryCategory: String = "Other",
    val subCategory: String = "Miscellaneous",
    val note: String = ""
) {
    val category: String get() = "$primaryCategory → $subCategory"
}